import React, { useState, useRef, useEffect, useCallback, useMemo, Suspense } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { User, Calendar, Activity, Target, List, Settings, ChevronLeft, ChevronRight, MessageSquare, Sun, Moon, FileText, Dumbbell, MoreHorizontal, X } from 'lucide-react';
import { IntervalsAPI } from './intervalsAPI';
import { StravaAPI } from './stravaAPI';
import { AICoach } from './aiIntegration';
import { getMockData } from './mockData';
import { sanitizeTrainingPlan, decoratePhase, decorateMilestone } from './utils/trainingPlanParser';
import { parseCoachActions, parseStructuredEnvelope, dispatchToolCalls } from './utils/coachActions';
import { computeTrainingSummary } from './utils/trainingSummary';
import { computeSessionSuggestions } from './utils/proactiveSuggestions';
import { computeACWRAlert, computeDisciplineGap } from './utils/proactiveAlerts';
import { enrichWorkoutsWithTSS } from './utils/tssEstimator';
import { sanitizeRaces, mergeRaceEntries, raceKey, getUpcomingRaces } from './utils/racesLifecycle';
import {
  mergeNoteEntries,
  shouldCompact,
  findStaleTransientCandidate,
  reconcileMemory,
  applyCompaction,
  addTombstone,
  dedupeNoteEntries,
  applyTombstones,
  mergeTombstones,
  injuryKey,
  addFactTombstone,
  applyFactTombstones,
  mergeFactTombstones,
  buildClearMemoryTombstones,
  mergeStructuredFactUpdates,
} from './utils/coachingMemory';
import {
  readPendingOnboarding,
  clearPendingOnboarding,
  applyGoalSeed,
  getProfileStorageKey,
  getUserScope,
  goalSeededFlagKey,
  welcomeShownFlagKey,
} from './utils/onboarding';
import SplashPage from './SplashPage';
import ChatPanel, { ChatToggleButton, ChatOverlay } from './ChatPanel';
import logoSrc from './assets/tri-harder-2.svg';
import pwrdByStravaWhiteApp from './assets/api_logo_pwrdBy_strava_horiz_white.svg';

// Lazy-loaded page components for code splitting
const SettingsPage = React.lazy(() => import('./SettingsPage'));
const DashboardPage = React.lazy(() => import('./DashboardPage'));
const CalendarPage = React.lazy(() => import('./CalendarPage'));
const ProfilePage = React.lazy(() => import('./ProfilePage'));
const AboutPage = React.lazy(() => import('./AboutPage'));
const PrivacyPage = React.lazy(() => import('./PrivacyPage'));
const SupportPage = React.lazy(() => import('./SupportPage'));
const AdminPage = React.lazy(() => import('./AdminPage'));
const ActivitiesPage = React.lazy(() => import('./ActivitiesPage'));
const TrainingPlanPage = React.lazy(() => import('./TrainingPlanPage'));
const StrengthPage = React.lazy(() => import('./StrengthPage'));
const ReportModal = React.lazy(() => import('./components/ReportModal'));
const ActivityDetailModal = React.lazy(() => import('./components/ActivityDetailModal'));
const WelcomeModal = React.lazy(() => import('./components/WelcomeModal'));

const LoadingSpinner = () => (
  <div className="flex-1 flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-border-focus"></div>
  </div>
);

const VALID_PAGES = ['chat', 'dashboard', 'calendar', 'trainingplan', 'activities', 'strength', 'profile', 'settings', 'about', 'privacy', 'support'];

/**
 * Returns a stable per-user scope string for localStorage keys.
 * Priority: Strava ID > intervals.icu OAuth ID > intervals API key athlete ID > null (anonymous).
 * Using a prefixed scope for Intervals users avoids collisions with Strava numeric IDs.
 * Anonymous users (no connected source) fall back to null → global key.
 */
function getStorageScope(creds) {
  if (!creds) return null;
  if (creds.stravaAthleteId) return creds.stravaAthleteId;
  if (creds.intervalsAthleteId) return `int_${creds.intervalsAthleteId}`;
  if (creds.athleteId) return `int_${creds.athleteId}`;
  return null;
}

/** Build a scoped localStorage key, or a bare key for anonymous/unknown users. */
function storageKey(base, scope) {
  return scope ? `${base}_${scope}` : base;
}

/**
 * Cheap structural comparison of two coaching-memory objects. Used to bail out
 * of a state update when a periodic sync produced no real change, so background
 * polling doesn't churn React state (and re-trigger the memory-save effect)
 * every tick. Compares the fields that actually vary: lastUpdated, scalar
 * fields, races/injuries, and a stable signature of noteEntries.
 */
function memoryEquivalent(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if ((a.lastUpdated || null) !== (b.lastUpdated || null)) return false;
  for (const f of ['fitnessLevel', 'goals', 'preferences', 'strengthPattern']) {
    if ((a[f] || '') !== (b[f] || '')) return false;
  }
  if (JSON.stringify(a.races || []) !== JSON.stringify(b.races || [])) return false;
  if (JSON.stringify(a.pastRaces || []) !== JSON.stringify(b.pastRaces || [])) return false;
  if (JSON.stringify(a.injuries || []) !== JSON.stringify(b.injuries || [])) return false;
  const tomb = (m) => (m.deletedNotes || [])
    .map(t => `${t.key || t.text}|${t.deletedAt}`)
    .sort()
    .join('\n');
  if (tomb(a) !== tomb(b)) return false;
  const factTomb = (m) => (m.deletedFacts || [])
    .map(t => `${t.kind}|${t.key}|${t.deletedAt}`)
    .sort()
    .join('\n');
  if (factTomb(a) !== factTomb(b)) return false;
  const sig = (m) => (m.noteEntries || [])
    .map(e => `${e.id}|${e.text}|${e.kind}|${e.lastConfirmedAt || ''}|${e.deferredUntil || ''}`)
    .sort()
    .join('\n');
  return sig(a) === sig(b);
}

// The 8 leaf sections that make up `athleteProfile` / server `profileMetrics`.
// Shared by the dirty-field diff below and mirrored server-side in
// api/coachingData/index.js's PROFILE_SECTIONS — keep both lists in sync.
const PROFILE_SYNC_SECTIONS = [
  'personal', 'background', 'metrics', 'goals',
  'schedule', 'equipment', 'health', 'lifestyle',
];

/** Array-aware "no meaningful value" check (schedule.availableDays is an array; everything else is a scalar string). */
function isEmptyProfileFieldValue(v) {
  return Array.isArray(v) ? v.length === 0 : (v === undefined || v === null || v === '');
}

/** Array-aware equality for a single profile leaf field. */
function profileFieldValuesEqual(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
  }
  return (a === undefined || a === null ? '' : a) === (b === undefined || b === null ? '' : b);
}

/**
 * Diff two profile snapshots into a { section: [changedFieldKey, ...] } map —
 * the "touchedFields" manifest the server's per-field merge (Issue #7 follow-up)
 * uses to tell an EXPLICIT edit (including clearing a field to '' or []) apart
 * from a merely carried-over, possibly-stale snapshot value. Only fields whose
 * value actually differs between `prev` (the last-known/synced profile) and
 * `next` (what the user is about to save) are reported — this is what lets an
 * intentional clear win server-side while an untouched field can never be wiped
 * by a fresh/partially-synced device's blank defaults.
 */
function computeTouchedProfileFields(prev, next) {
  const touched = {};
  for (const section of PROFILE_SYNC_SECTIONS) {
    const prevSection = (prev && prev[section]) || {};
    const nextSection = (next && next[section]) || {};
    const keys = new Set([...Object.keys(prevSection), ...Object.keys(nextSection)]);
    const changed = [];
    for (const k of keys) {
      if (!profileFieldValuesEqual(prevSection[k], nextSection[k])) changed.push(k);
    }
    if (changed.length) touched[section] = changed;
  }
  return touched;
}

// ── Coaching-session token cache (per athlete) ───────────────────────────────
// The server issues a short-lived HMAC-signed session token after validating
// the Strava token once; we cache it per athlete and present it on subsequent
// /api/coachingData calls so the server no longer hits Strava (and its rate
// limit) on every request. Keyed per athlete to avoid cross-account bleed on a
// shared browser; the server independently re-verifies the embedded athlete id.
const COACH_SESSION_PREFIX = 'triHarderCoachSession';
// Sentinel returned by the bootstrap mint attempt when the server reports Strava
// is rate-limiting us (HTTP 429). Distinct from a rejected token (try next) so
// the caller can back off instead of trying every remaining token holder.
const COACH_RATE_LIMITED = Symbol('coach_rate_limited');
// How long to suppress new bootstrap attempts after a 429 (Strava's short-term
// window is 15 min; we re-probe sooner in case it clears early or was per-call).
const COACH_SESSION_COOLDOWN_MS = 90 * 1000;
function readCoachSession(athleteId) {
  if (!athleteId) return null;
  try { return localStorage.getItem(`${COACH_SESSION_PREFIX}_${athleteId}`) || null; } catch { return null; }
}
function writeCoachSession(athleteId, token) {
  if (!athleteId || !token) return;
  try { localStorage.setItem(`${COACH_SESSION_PREFIX}_${athleteId}`, token); } catch { /* ignore quota */ }
}
function clearCoachSessions(athleteId) {
  try {
    if (athleteId) {
      localStorage.removeItem(`${COACH_SESSION_PREFIX}_${athleteId}`);
      return;
    }
    Object.keys(localStorage)
      .filter(k => k.startsWith(`${COACH_SESSION_PREFIX}_`))
      .forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

/**
 * Fire-and-forget telemetry for Coach Actions events.
 * Only called when a Strava athlete ID is present (OAuth users only — consistent
 * with the privacy promise that API-key users get device-only storage).
 */
function postCoachActionsTelemetry(payload) {
  fetch('/api/trackCoachActions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

const TriathlonCoachApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = useMemo(() => {
    const slug = location.pathname.replace(/^\//, '') || 'dashboard';
    return VALID_PAGES.includes(slug) ? slug : 'dashboard';
  }, [location.pathname]);
  const setCurrentPage = useCallback((page) => navigate(`/${page}`), [navigate]);

  /** Called from StrengthPage when user taps "Ask coach about this" or "Generate my routine". */
  const handleAskCoach = useCallback((prompt) => {
    setInputMessage(prompt);
    navigate('/chat');
  }, [navigate]);

  const [messages, setMessages] = useState([]);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [coachBriefing, setCoachBriefing] = useState(null);
  const [coachBriefingLoading, setCoachBriefingLoading] = useState(false);
  const [chatSidebarOpen, setChatSidebarOpen] = useState(true);
  const [chatSidebarExpanded, setChatSidebarExpanded] = useState(false);
  const [chatOverlayOpen, setChatOverlayOpen] = useState(false);
  const [navSidebarCollapsed, setNavSidebarCollapsed] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('triHarderTheme') || 'dark');
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('triHarderTheme', next);
  };

  // Show splash page until any auth is connected
  const [showSplash, setShowSplash] = useState(() => {
    try {
      const creds = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
      return !creds.stravaAccessToken && !creds.intervalsAccessToken && !(creds.apiKey && creds.athleteId);
    } catch {
      return true;
    }
  });
  const [hasInitializedAppData, setHasInitializedAppData] = useState(() => {
    try {
      const creds = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
      return !creds.stravaAccessToken && !creds.intervalsAccessToken && !(creds.apiKey && creds.athleteId);
    } catch {
      return true;
    }
  });

  const [isDemoMode, setIsDemoMode] = useState(false);

  // User credentials state - support both Intervals.icu and Strava
  const [userCredentials, setUserCredentials] = useState({
    // Intervals.icu credentials
    apiKey: '',
    athleteId: '',
    isConfigured: false,

    // Strava OAuth credentials
    stravaAccessToken: '',
    stravaRefreshToken: '',
    stravaExpiresAt: null,
    stravaAthleteId: '',
    isStravaConfigured: false,

    // Intervals.icu OAuth2 credentials
    intervalsAccessToken: '',
    intervalsRefreshToken: '',
    intervalsExpiresAt: null,
    intervalsAthleteId: '',
    isIntervalsOAuthConfigured: false,

    // AI provider: 'openai' or 'azure'
    aiProvider: 'openai',
    openaiApiKey: '',
    azureEndpoint: '',
    azureApiVersion: '2025-01-01-preview',
    azureDeployment: 'gpt-4o-mini',
    isOpenAIConnected: false,

    // Active data source
    activeDataSource: 'none' // 'intervals', 'strava', or 'none'
  });
  const [athleteProfile, setAthleteProfile] = useState({
    personal: {
      name: '',
      age: '',
      gender: '',
      weight: '',
      height: ''
    },
    background: {
      sportFocus: 'triathlon',
      experienceLevel: '',
      yearsInTriathlon: '',
      weeklyVolume: '',
      trainingStatus: '',
      sportsBackground: ''
    },
    metrics: {
      ftp: '',
      thresholdPace: '',
      css: '',
      maxHR: '',
      lthr: '',
      restingHR: ''
    },
    goals: {
      seasonGoals: '',
      targetRaces: ''
    },
    schedule: {
      availableDays: [],
      preferredTimes: '',
      weeklyHours: '',
      blackoutPeriods: ''
    },
    equipment: {
      poolAccess: '',
      bikeType: '',
      trainer: '',
      gymAccess: ''
    },
    health: {
      currentInjuries: '',
      injuryHistory: '',
      medicalConditions: ''
    },
    lifestyle: {
      averageSleep: '',
      sleepQuality: '',
      stressLevel: '',
      nutritionApproach: '',
      recoveryPractices: ''
    }
  });
  
  const EMPTY_MEMORY = { races: [], pastRaces: [], injuries: [], fitnessLevel: '', goals: '', preferences: '', strengthPattern: '', notes: '', noteEntries: [], deletedNotes: [], deletedFacts: [], lastUpdated: null };
  const [coachingMemory, setCoachingMemory] = useState(EMPTY_MEMORY);
  const [privacyMode, setPrivacyMode] = useState('full'); // 'full' | 'session'
  const [analyticsOptIn, setAnalyticsOptIn] = useState(false); // share chat logs for owner analysis
  const [coachStyle, setCoachStyle] = useState('balanced'); // 'direct' | 'balanced' | 'motivational'
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [completionModal, setCompletionModal] = useState({ open: false, workoutId: null, rpe: null, note: '' });
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedActivityForDetail, setSelectedActivityForDetail] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [pendingWelcome, setPendingWelcome] = useState(false);
  const [pendingWelcomeGoal, setPendingWelcomeGoal] = useState(null);
  const [attachedActivityIds, setAttachedActivityIds] = useState([]);
  const [savedQuestions, setSavedQuestions] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
      const key = `triHarderSavedQuestions_${stored.stravaAthleteId || stored.intervalsAthleteId || 'default'}`;
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch { return []; }
  });
  const [dataError, setDataError] = useState('');
  const [profileSummary, setProfileSummary] = useState('');
  const [athleteGear, setAthleteGear] = useState({ bikes: [], shoes: [] });
  // Fields prefilled from Strava this session (shown as an import notice on Profile page)
  const [stravaImportedFields, setStravaImportedFields] = useState(null);
  const [athleteZones, setAthleteZones] = useState(null);
  const [wellnessData, setWellnessData] = useState(null);
  const [activityRatings, setActivityRatings] = useState({});
  const [unratedNewActivities, setUnratedNewActivities] = useState([]);
  const [gearTags, setGearTags] = useState({});

  // Persist saved questions scoped to athlete ID
  const saveSavedQuestions = useCallback((questions) => {
    setSavedQuestions(questions);
    const athleteId = userCredentials.stravaAthleteId || userCredentials.intervalsAthleteId || 'default';
    localStorage.setItem(`triHarderSavedQuestions_${athleteId}`, JSON.stringify(questions));
  }, [userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId]);

  // Reload activity ratings from localStorage when source/athlete scope changes
  useEffect(() => {
    let key = null;
    if (userCredentials.activeDataSource === 'strava' && userCredentials.stravaAthleteId) {
      key = `triHarderActivityRatings_strava_${userCredentials.stravaAthleteId}`;
    } else if (userCredentials.activeDataSource === 'intervals' && userCredentials.intervalsAthleteId) {
      key = `triHarderActivityRatings_intervals_${userCredentials.intervalsAthleteId}`;
    }
    if (!key) { setActivityRatings({}); return; }
    try {
      setActivityRatings(JSON.parse(localStorage.getItem(key) || '{}'));
    } catch { setActivityRatings({}); }
  }, [userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId, userCredentials.activeDataSource]);

  // Save a feel rating for a specific activity and remove it from the prompt queue
  const rateActivity = useCallback((id, feel) => {
    let key = null;
    if (userCredentials.activeDataSource === 'strava' && userCredentials.stravaAthleteId) {
      key = `triHarderActivityRatings_strava_${userCredentials.stravaAthleteId}`;
    } else if (userCredentials.activeDataSource === 'intervals' && userCredentials.intervalsAthleteId) {
      key = `triHarderActivityRatings_intervals_${userCredentials.intervalsAthleteId}`;
    }
    setActivityRatings(prev => {
      const next = { ...prev, [String(id)]: { feel, ratedAt: new Date().toISOString() } };
      if (key) { try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* ignore */ } }
      return next;
    });
    setUnratedNewActivities(prev => prev.filter(w => String(w.id) !== String(id)));
  }, [userCredentials.activeDataSource, userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId]);

  const dismissRpePrompt = useCallback(() => setUnratedNewActivities([]), []);
  
  // Profile completion state
  const [profileCompletion, setProfileCompletion] = useState({
    isActive: false,
    currentStep: 0,
    answers: {}
  });
  const messagesEndRef = useRef(null);
  const conversationRestoredRef = useRef(false);
  const sessionOpenerFiredRef = useRef(false);
  const checkinFiredRef = useRef(false);
  const briefingFiredRef = useRef(false);
  const alertFiredRef = useRef(false);
  const nudgeFiredRef = useRef(false);
  const racePrepFiredRef = useRef(false);
  // Incremented by startNewConversation; proactive AI callbacks check it to
  // discard results that arrived after the session was reset.
  const sessionCountRef = useRef(0);

  // Stable id for the CURRENT chat session, used to upsert one analytics row per
  // session (opt-in only). Regenerated whenever a new conversation starts.
  const analyticsSessionIdRef = useRef(`s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);

  // Tracks the current activeDataSource without being a useCallback dependency.
  // Allows loadWorkoutData to be a stable (zero-dep) callback while still
  // reading the latest source when called without an explicit override.
  const activeDataSourceRef = useRef('none');

  // Monotonic id for workout loads. Overlapping loads (e.g. a data-source switch
  // while a prior load is still in flight) can otherwise finish out of order and
  // a slower/older response can clobber newer data. Each load captures its id and
  // bails before committing state if a newer load has started.
  const loadWorkoutReqIdRef = useRef(0);

  // Holds the in-flight Strava token refresh promise so concurrent callers
  // (saveToServer memory + saveToServer conversation + syncFromServer all fire
  // at page load) share ONE refresh instead of each racing their own. Strava
  // rotates the refresh token on every refresh and invalidates the prior one,
  // so concurrent refreshes brick each other's refresh token → permanent 401s
  // on /api/coachingData. See CLAUDE.md "Strava dual-refresh-path gotcha".
  const tokenRefreshPromiseRef = useRef(null);

  // Single-flight guard for the coaching-session bootstrap: the cold-start burst
  // of coachingData calls (memory save + conversation save + sync) would each
  // trigger a separate Strava-validated mint. This coalesces them so the burst
  // makes AT MOST ONE Strava validation; the rest reuse the minted token.
  const coachSessionPromiseRef = useRef(null);

  // Rate-limit backoff: when Strava throttles the server's bootstrap validation
  // (server returns HTTP 429), we stop attempting new bootstraps until this
  // timestamp passes. Each failed bootstrap costs a Strava /api/v3/athlete call,
  // so retry-storming during a throttle only deepens the limit (Strava: 200
  // requests / 15 min per app). The 12h session token means we only need ONE
  // successful mint once the window clears.
  const coachSessionCooldownRef = useRef(0);

  // Initialize API instances
  const intervalsAPI = useRef(null);
  const stravaAPI = useRef(null);
  const aiCoach = useRef(null);
  const [workoutData, setWorkoutData] = useState({
    recentWorkouts: [],
    plannedWorkouts: [],
    isLoading: false
  });

  // Enrich recent workouts with self-reported feel ratings so all AI contexts see them
  const recentWorkoutsEnriched = useMemo(() =>
    workoutData.recentWorkouts.map(w => ({
      ...w,
      feeling: activityRatings[String(w.id)]?.feel ?? w.feeling,
    })),
    [workoutData.recentWorkouts, activityRatings]
  );

  // Workouts with estimated TSS filled in for activities missing Strava Relative Effort
  // (e.g. free Strava accounts). Estimated entries are flagged tssEstimated:true.
  const tssEnrichedWorkouts = useMemo(() =>
    enrichWorkoutsWithTSS(workoutData.recentWorkouts, athleteProfile.metrics),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workoutData.recentWorkouts, athleteProfile.metrics]
  );

  // Training calendar planned workouts (AI-generated)
  const [plannedWorkouts, setPlannedWorkouts] = useState([]);

  // Season training plan (phases + milestones, AI-only editing)
  const [trainingPlan, setTrainingPlan] = useState({ phases: [], milestones: [] });

  // Next upcoming race (derived from coaching memory).
  // Uses the shared getUpcomingRaces helper so DNF/DNS-tagged races on a
  // future date, and past-dated entries that haven't yet been sanitized,
  // don't wrongly drive the countdown pill or briefing race-week logic.
  const nextRace = useMemo(() => {
    const upcoming = getUpcomingRaces(coachingMemory);
    if (!upcoming.length) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    // getUpcomingRaces includes fuzzy-date entries; filter to ones we can
    // actually count down to before sorting.
    const dated = upcoming
      .filter(r => /^\d{4}-\d{2}-\d{2}$/.test(r.date || ''))
      .sort((a, b) => a.date.localeCompare(b.date));
    if (!dated.length) return null;
    const [y, m, d] = dated[0].date.split('-').map(Number);
    const raceLocal = new Date(y, m - 1, d);
    return { ...dated[0], daysUntil: Math.round((raceLocal - today) / 86400000) };
  }, [coachingMemory]);

  // Refresh the Strava access token using the stored refresh token.
  // Updates localStorage, userCredentials state, and aiCoach.stravaToken.
  // Returns the new access token on success, null on failure.
  //
  // Single-flight: concurrent callers share one in-flight refresh so we never
  // fire two refreshes with the same refresh token (Strava rotates + invalidates
  // it on each use, which would brick the second caller and cause cascading 401s).
  const refreshStravaToken = useCallback(async () => {
    if (tokenRefreshPromiseRef.current) {
      return tokenRefreshPromiseRef.current;
    }
    const run = (async () => {
      try {
        const saved = localStorage.getItem('triHarderCredentials');
        if (!saved) return null;
        const creds = JSON.parse(saved);
        if (!creds.stravaRefreshToken) return null;

        const tempStrava = new StravaAPI();
        tempStrava.refreshToken = creds.stravaRefreshToken;
        const result = await tempStrava.refreshAccessToken();

        if (result.success) {
          // Re-read the latest creds in case another flow mutated them while
          // the network refresh was in flight, then overlay the rotated tokens.
          const latest = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
          // Same-session guard: if the user switched account or reconnected
          // while this refresh was in flight, the rotated tokens belong to a
          // stale session — discard rather than clobber the new credentials.
          if (latest.stravaAthleteId && creds.stravaAthleteId &&
              latest.stravaAthleteId !== creds.stravaAthleteId) {
            return null;
          }
          const updated = {
            ...latest,
            stravaAccessToken: result.accessToken,
            stravaRefreshToken: result.refreshToken,
            stravaExpiresAt: result.expiresAt
          };
          localStorage.setItem('triHarderCredentials', JSON.stringify(updated));
          setUserCredentials(prev => ({
            ...prev,
            stravaAccessToken: result.accessToken,
            stravaRefreshToken: result.refreshToken,
            stravaExpiresAt: result.expiresAt
          }));
          if (aiCoach.current) {
            aiCoach.current.stravaToken = result.accessToken;
          }
          // Push the rotated tokens into the live StravaAPI instance too, so
          // its next internal ensureValidToken refresh doesn't reuse the now-
          // invalidated refresh token (the reverse direction of the
          // persistStravaTokenRotation callback).
          if (stravaAPI.current) {
            stravaAPI.current.accessToken = result.accessToken;
            stravaAPI.current.refreshToken = result.refreshToken;
            stravaAPI.current.expiresAt = result.expiresAt;
          }
          return result.accessToken;
        }
        // Our refresh failed — most likely the other refresh path (the live
        // StravaAPI instance) already rotated the token with the same refresh
        // token, invalidating ours. If localStorage now holds a still-valid
        // access token, hand that back rather than reporting failure.
        const after = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
        if (after.stravaAccessToken &&
            after.stravaAccessToken !== creds.stravaAccessToken &&
            after.stravaExpiresAt && Date.now() < (after.stravaExpiresAt - 5 * 60 * 1000)) {
          // Sync the winning token into the live instances + state too. Without
          // this, aiCoach/stravaAPI keep OUR now-dead token and the next
          // /api/coachingData Bearer 401s (the 401-cascade family of bugs).
          if (aiCoach.current) aiCoach.current.stravaToken = after.stravaAccessToken;
          if (stravaAPI.current) {
            stravaAPI.current.accessToken = after.stravaAccessToken;
            if (after.stravaRefreshToken) stravaAPI.current.refreshToken = after.stravaRefreshToken;
            if (after.stravaExpiresAt) stravaAPI.current.expiresAt = after.stravaExpiresAt;
          }
          setUserCredentials(prev => ({
            ...prev,
            stravaAccessToken: after.stravaAccessToken,
            stravaRefreshToken: after.stravaRefreshToken || prev.stravaRefreshToken,
            stravaExpiresAt: after.stravaExpiresAt || prev.stravaExpiresAt,
          }));
          return after.stravaAccessToken;
        }
        return null;
      } catch {
        return null;
      }
    })();
    tokenRefreshPromiseRef.current = run;
    try {
      return await run;
    } finally {
      tokenRefreshPromiseRef.current = null;
    }
  }, []);

  // Returns a fresh Strava token from localStorage, refreshing if needed.
  // Does not update React state — use refreshStravaToken() for that.
  const getFreshToken = useCallback(async () => {
    try {
      const stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
      const expiresAt = stored.stravaExpiresAt;
      if (!expiresAt || Date.now() > (expiresAt - 10 * 60 * 1000)) {
        return await refreshStravaToken();
      }
      return stored.stravaAccessToken || null;
    } catch {
      return null;
    }
  }, [refreshStravaToken]);

  // Returns the raw Strava access tokens currently held by the two client token
  // holders, WITHOUT triggering any refresh: the localStorage copy and the live
  // StravaAPI instance. De-duplicated, localStorage first. We deliberately avoid
  // getFreshToken()'s refresh-if-near-expiry behaviour here because refreshing is
  // precisely what triggers the token-rotation race that can brick BOTH holders
  // (Strava invalidates the prior refresh token on each use). For a one-shot
  // session-token mint we only need a token that is valid *right now*; the 12h
  // session token then decouples coachingData from the access token's lifetime.
  const getRawStravaTokens = useCallback(() => {
    const tokens = [];
    const push = (t) => { if (t && !tokens.includes(t)) tokens.push(t); };
    try {
      const stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
      push(stored.stravaAccessToken);
    } catch { /* ignore malformed creds */ }
    const inst = stravaAPI.current;
    if (inst) push(inst.accessToken);
    return tokens;
  }, []);

  // Single best-guess Strava bearer for the no-session-token POST/GET fallback
  // paths (saveToServer / syncFromServer). Prefers a raw, already-valid token and
  // only falls back to getFreshToken() — which may refresh — when neither holder
  // has a token. The session-token bootstrap in ensureCoachSession is the robust
  // path: it tries every raw token, then a refreshed token, until one validates.
  const getStravaBearer = useCallback(async () => {
    const raw = getRawStravaTokens();
    if (raw[0]) return raw[0];
    return await getFreshToken();
  }, [getRawStravaTokens, getFreshToken]);

  // Ensure a server-signed coaching-session token exists for this athlete,
  // coalescing concurrent bootstrap attempts (see coachSessionPromiseRef). On a
  // cold cache this makes ONE Strava-validated GET to mint the token; every
  // subsequent coachingData call then authenticates statelessly via the token,
  // so the server never re-hits Strava (and its rate limit). Returns the token
  // or null (null → callers fall back to Bearer-token auth).
  const ensureCoachSession = useCallback(async (athleteId) => {
    if (!athleteId) return null;
    const cached = readCoachSession(athleteId);
    if (cached) return cached;
    // Respect the rate-limit cooldown: while Strava is throttling us, making more
    // bootstrap validations only deepens the limit. Callers get null and fall
    // back to (or skip) Bearer auth until the window clears.
    if (Date.now() < coachSessionCooldownRef.current) return null;
    if (coachSessionPromiseRef.current) return coachSessionPromiseRef.current;
    const run = (async () => {
      try {
        // Attempt to mint a session token with a given bearer. Returns the minted
        // token, null (authenticated but server didn't mint — HMAC_SECRET unset),
        // undefined (token rejected → caller should try the next candidate), or
        // COACH_RATE_LIMITED (Strava throttled → stop and back off).
        const tryMint = async (bearer) => {
          const res = await fetch('/api/coachingData', {
            headers: { 'Authorization': `Bearer ${bearer}`, 'X-Strava-Token': bearer }
          });
          if (res.status === 429) {
            // Strava is rate-limiting the server's validation. Back off and stop
            // trying further tokens — they'd all hit the same throttled endpoint.
            coachSessionCooldownRef.current = Date.now() + COACH_SESSION_COOLDOWN_MS;
            return COACH_RATE_LIMITED;
          }
          if (!res.ok) return undefined; // stale/invalid token → try the next one
          const data = await res.json().catch(() => null);
          if (data?.sessionToken) {
            writeCoachSession(athleteId, data.sessionToken);
            return data.sessionToken;
          }
          return null;
        };

        // 1. Try the raw, already-held tokens first (no refresh → no rotation
        //    race). Whichever holder is currently valid mints the session.
        const rawTokens = getRawStravaTokens();
        for (const bearer of rawTokens) {
          const result = await tryMint(bearer);
          if (result === COACH_RATE_LIMITED) return null; // backed off
          if (result !== undefined) return result;
        }

        // 2. Last resort: a refreshed token. Only reached when every raw token is
        //    already rejected by Strava (e.g. all expired). getFreshToken()
        //    single-flights the refresh and reverse-syncs the instance.
        const fresh = await getFreshToken();
        if (fresh && !rawTokens.includes(fresh)) {
          const result = await tryMint(fresh);
          if (result === COACH_RATE_LIMITED) return null;
          if (result !== undefined) return result;
        }
        return null; // no token validated
      } catch {
        return null;
      } finally {
        coachSessionPromiseRef.current = null;
      }
    })();
    coachSessionPromiseRef.current = run;
    return run;
  }, [getRawStravaTokens, getFreshToken]);

  // Fetch /api/coachingData with session-token-preferred auth and ONE automatic
  // recovery when a cached session token has gone stale. The server returns 401
  // for an expired/invalid X-Coach-Session (e.g. a returning user a day later,
  // past the 12h TTL); without recovery the client would keep presenting the dead
  // session token and never re-bootstrap → a fresh 401 loop. On a 401 that used a
  // session token we clear it, force a fresh Bearer-validated bootstrap, and retry
  // once. Returns { ok, data } (data = parsed JSON body or null). Any rolled or
  // minted sessionToken on the response is cached.
  const coachingDataFetch = useCallback(async (athleteId, init = {}) => {
    if (!athleteId) return { ok: false, data: null };

    const send = async (session, bearer) => {
      const headers = { ...(init.headers || {}) };
      if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
      if (bearer) headers['X-Strava-Token'] = bearer;
      if (session) headers['X-Coach-Session'] = session;
      return fetch('/api/coachingData', { ...init, headers });
    };

    let session = await ensureCoachSession(athleteId);
    let bearer = session ? null : await getStravaBearer();
    if (!session && !bearer) return { ok: false, data: null };

    let res;
    try {
      res = await send(session, bearer);
    } catch {
      return { ok: false, data: null };
    }

    // Strava is throttling the server's bootstrap validation. Record the cooldown
    // so subsequent calls skip the (futile, limit-deepening) bootstrap, and bail.
    if (res.status === 429) {
      coachSessionCooldownRef.current = Date.now() + COACH_SESSION_COOLDOWN_MS;
      return { ok: false, data: null };
    }

    // Stale cached session → drop it, re-bootstrap with a raw Bearer, retry once.
    if (res.status === 401 && session) {
      clearCoachSessions(athleteId);
      coachSessionPromiseRef.current = null; // allow a brand-new bootstrap run
      session = await ensureCoachSession(athleteId);
      bearer = session ? null : await getStravaBearer();
      if (!session && !bearer) return { ok: false, data: null };
      try {
        res = await send(session, bearer);
      } catch {
        return { ok: false, data: null };
      }
      if (res.status === 429) {
        coachSessionCooldownRef.current = Date.now() + COACH_SESSION_COOLDOWN_MS;
        return { ok: false, data: null };
      }
    }

    if (!res.ok) return { ok: false, data: null };
    const data = await res.json().catch(() => null);
    if (data?.sessionToken) writeCoachSession(athleteId, data.sessionToken);
    return { ok: true, data };
  }, [ensureCoachSession, getStravaBearer]);

  // POST data to the server API (no-op if Strava not connected or call fails).
  // Auth + stale-session recovery is handled by coachingDataFetch. Returns the
  // { ok, data } result so callers can read the server's response (e.g. the
  // merged memory echoed back on a memory save).
  const saveToServer = useCallback(async (data) => {
    if (!userCredentials.isStravaConfigured || !userCredentials.stravaAthleteId) {
      return { ok: false, data: null };
    }
    return coachingDataFetch(userCredentials.stravaAthleteId, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  }, [userCredentials.isStravaConfigured, userCredentials.stravaAthleteId, coachingDataFetch]);

  // Persist conversation to localStorage (and server if Strava connected, skipped in session mode)
  useEffect(() => {
    if (!conversationRestoredRef.current) return undefined;
    if (privacyMode === 'session') return undefined;
    if (messages.length <= 1) return undefined;
    const { stravaAthleteId, intervalsAthleteId, athleteId } = userCredentials;
    const scope = getStorageScope({ stravaAthleteId, intervalsAthleteId, athleteId });
    localStorage.setItem(storageKey('triHarderConversation', scope), JSON.stringify(messages));
    // Debounce the SERVER write (the localStorage write above stays immediate).
    // During AI streaming `messages` updates on every revealed word; without this
    // each word fired a /api/coachingData POST (hundreds per response), and in the
    // cold-start auth window — before a session token is minted — EACH POST cost a
    // Strava /api/v3/athlete token validation. That alone can exhaust Strava's
    // daily request budget (a real incident: 6000+ reads in a day). Coalesce to a
    // single save ~2.5s after streaming settles. The cleanup clears the prior timer
    // on each change, so only the final state is persisted to the server.
    const timer = setTimeout(() => { saveToServer({ conversation: messages }); }, 2500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId, userCredentials.athleteId, privacyMode, saveToServer]);

  // Persist coaching memory to localStorage and server
  useEffect(() => {
    if (!coachingMemory.lastUpdated) return;
    const { stravaAthleteId, intervalsAthleteId, athleteId } = userCredentials;
    const scope = getStorageScope({ stravaAthleteId, intervalsAthleteId, athleteId });
    localStorage.setItem(storageKey('triHarderMemory', scope), JSON.stringify(coachingMemory));
    // The server merges our push with the stored memory and echoes the union
    // back; adopt it so this device immediately picks up notes another device
    // added since we last pulled. The change-guard keeps this bounded — an
    // unchanged re-save resolves as equivalent and the effect stops re-firing.
    saveToServer({ memory: coachingMemory }).then(res => {
      const serverMemory = res?.data?.memory;
      if (!serverMemory?.lastUpdated) return;
      setCoachingMemory(prev => {
        const merged = reconcileMemory(serverMemory);
        return memoryEquivalent(prev, merged) ? prev : merged;
      });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachingMemory, userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId, userCredentials.athleteId, saveToServer]);

  // Opt-in chat analytics: when the user has explicitly enabled it in Settings,
  // debounce-post the current session transcript to /api/logChat so the app
  // owner can review real conversations to troubleshoot and improve UX. Mirrors
  // the conversation-save debounce so a streaming response doesn't fire per word.
  // Requires Strava — the endpoint authenticates with the signed coach-session
  // token (the athlete ID is derived server-side from the verified token, so the
  // client can't spoof it and the endpoint can't be spammed anonymously).
  useEffect(() => {
    if (!analyticsOptIn) return undefined;
    if (messages.length <= 1) return undefined;
    const athleteId = userCredentials.stravaAthleteId;
    if (!athleteId) return undefined;
    const snapshot = messages;
    const timer = setTimeout(async () => {
      const session = await ensureCoachSession(athleteId);
      if (!session) return; // no valid session (or rate-limited) → skip this tick
      fetch('/api/logChat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Coach-Session': session },
        body: JSON.stringify({
          optIn: true,
          sessionId: analyticsSessionIdRef.current,
          coachStyle,
          // Optional provenance label. Automated verification runs set
          // `triHarderAnalyticsTag` so their synthetic conversations can be
          // filtered out of real-usage analysis; organic usage is untagged.
          tag: localStorage.getItem('triHarderAnalyticsTag') || undefined,
          messages: snapshot,
        }),
      }).catch(() => {}); // fire-and-forget — never block the UI
    }, 3000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, analyticsOptIn, userCredentials.stravaAthleteId, coachStyle, ensureCoachSession]);

  // Persist gear tags to localStorage
  useEffect(() => {
    if (!Object.keys(gearTags).length) return;
    const { stravaAthleteId, intervalsAthleteId, athleteId } = userCredentials;
    const scope = getStorageScope({ stravaAthleteId, intervalsAthleteId, athleteId });
    localStorage.setItem(storageKey('triHarderGearTags', scope), JSON.stringify(gearTags));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gearTags, userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId, userCredentials.athleteId]);

  // Persist planned workouts to localStorage.
  // Always saves once init is complete — including empty arrays — so that
  // removing the last workout (e.g. via a confirmed remove_workouts proposal)
  // survives a reload. Without this, the old guard `length === 0 return`
  // resurrected the prior non-empty state from localStorage on next load.
  useEffect(() => {
    if (!hasInitializedAppData) return;
    const { stravaAthleteId, intervalsAthleteId, athleteId } = userCredentials;
    const scope = getStorageScope({ stravaAthleteId, intervalsAthleteId, athleteId });
    localStorage.setItem(storageKey('triHarderPlannedWorkouts', scope), JSON.stringify(plannedWorkouts));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedWorkouts, hasInitializedAppData, userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId, userCredentials.athleteId]);

  // Persist training plan to localStorage — same empty-state fix as above.
  useEffect(() => {
    if (!hasInitializedAppData) return;
    const { stravaAthleteId, intervalsAthleteId, athleteId } = userCredentials;
    const scope = getStorageScope({ stravaAthleteId, intervalsAthleteId, athleteId });
    localStorage.setItem(storageKey('triHarderTrainingPlan', scope), JSON.stringify(trainingPlan));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingPlan, hasInitializedAppData, userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId, userCredentials.athleteId]);

  // Auto-complete planned workouts based on Strava activities.
  // Each real activity can satisfy AT MOST ONE planned workout (so two planned
  // sessions of the same sport on one day aren't both flipped by a single ride),
  // and an earlier AUTO-completion reverts to 'planned' if its matching activity
  // disappears. Manual completions (status 'completed' without `autoCompleted`)
  // are never touched.
  useEffect(() => {
    if (plannedWorkouts.length === 0) return;

    const activitiesByDate = {};
    workoutData.recentWorkouts.forEach(a => {
      const d = new Date(a.date).toISOString().split('T')[0];
      (activitiesByDate[d] = activitiesByDate[d] || []).push(a);
    });

    const used = new Set(); // each activity object consumed at most once
    const findMatch = (planned) => {
      const pDate = new Date(planned.date).toISOString().split('T')[0];
      const pool = activitiesByDate[pDate] || [];
      const found = pool.find(a => !used.has(a) && a.type === planned.sport);
      if (found) used.add(found);
      return found || null;
    };

    const updated = plannedWorkouts.map(planned => {
      // Leave manual completions and non-planned statuses alone.
      if (planned.status !== 'planned' && !planned.autoCompleted) return planned;
      const match = findMatch(planned);
      if (match) {
        if (planned.status === 'completed' && planned.autoCompleted) return planned;
        return { ...planned, status: 'completed', autoCompleted: true };
      }
      // No matching activity: undo a previous auto-completion.
      if (planned.autoCompleted) {
        const { autoCompleted, ...rest } = planned;
        return { ...rest, status: 'planned' };
      }
      return planned;
    });

    if (JSON.stringify(updated) !== JSON.stringify(plannedWorkouts)) {
      setPlannedWorkouts(updated);
    }
  }, [workoutData.recentWorkouts, plannedWorkouts]);

  // Fetch coaching data from server and merge with local state
  const syncFromServer = useCallback(async () => {
    const stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
    const athleteId = stored.stravaAthleteId;
    if (!athleteId) return;
    const { ok, data } = await coachingDataFetch(athleteId);
    if (!ok || !data) return;
    try {

      // Memory: per-entry merge to avoid last-write-wins data loss
      if (data.memory?.lastUpdated) {
        setCoachingMemory(prev => {
          const incoming = reconcileMemory(data.memory);
          if (!prev.lastUpdated) return incoming;
          // Pick the base with the newer scalar fields (goals, fitnessLevel, …)
          const base = new Date(incoming.lastUpdated) >= new Date(prev.lastUpdated)
            ? incoming : prev;
          // Union tombstones from both sides FIRST so a deletion made on either
          // device suppresses the note here — deletion survives a union merge.
          const deletedNotes = mergeTombstones(prev.deletedNotes || [], incoming.deletedNotes || []);
          // Apply tombstones to the RAW union BEFORE semantic dedupe (server
          // parity — see reconcileMemory). Deduping first would fold a re-learned
          // post-deletion variant into the old note's earliest createdAt and get
          // it wrongly suppressed, diverging from the server which keeps it.
          const mergedEntries = dedupeNoteEntries(
            applyTombstones(
              mergeNoteEntries(
                prev.noteEntries || [],
                incoming.noteEntries || []
              ),
              deletedNotes
            )
          );
          // Per-entry merge of races and pastRaces, mirrored on both arrays,
          // so a device archival on device A isn't wiped by a scalar edit on
          // device B that happens to carry a newer lastUpdated. Then rerun
          // sanitizeRaces so cross-array dedup (archive-location-wins) stays
          // enforced regardless of which side the entry came from.
          const raceMap = new Map();
          for (const r of prev.races || [])     raceMap.set(raceKey(r), { ...r });
          for (const r of incoming.races || []) {
            const k = raceKey(r);
            const cur = raceMap.get(k);
            raceMap.set(k, cur ? mergeRaceEntries(cur, r) : { ...r });
          }
          const pastMap = new Map();
          for (const r of prev.pastRaces || [])     pastMap.set(raceKey(r), { ...r });
          for (const r of incoming.pastRaces || []) {
            const k = raceKey(r);
            const cur = pastMap.get(k);
            pastMap.set(k, cur ? mergeRaceEntries(cur, r) : { ...r });
          }
          // Per-key merge injuries too so a re-learned injury (carrying
          // memoryUpdatedAt) isn't dropped in favour of a stale copy.
          const injuryMap = new Map();
          for (const i of prev.injuries || []) injuryMap.set(injuryKey(i), { ...i });
          for (const i of incoming.injuries || []) {
            const k = injuryKey(i);
            const cur = injuryMap.get(k);
            const inT = new Date(i.memoryUpdatedAt || 0).getTime();
            const curT = cur ? new Date(cur.memoryUpdatedAt || 0).getTime() : -1;
            if (!cur || inT >= curT) injuryMap.set(k, cur ? { ...cur, ...i } : { ...i });
          }
          // Union structured-fact tombstones from both sides, then suppress any
          // deleted race/injury on the merged arrays — a race/injury deletion
          // survives the union, unless it was re-learned after the deletion.
          const deletedFacts = mergeFactTombstones(prev.deletedFacts || [], incoming.deletedFacts || []);
          const sanitized = sanitizeRaces({
            races: Array.from(raceMap.values()),
            pastRaces: Array.from(pastMap.values()),
          });
          const factFiltered = applyFactTombstones({
            races: sanitized.races,
            pastRaces: sanitized.pastRaces,
            injuries: Array.from(injuryMap.values()),
          }, deletedFacts);
          const next = {
            ...base,
            noteEntries: mergedEntries,
            deletedNotes,
            deletedFacts,
            races: factFiltered.races,
            pastRaces: factFiltered.pastRaces,
            injuries: factFiltered.injuries,
          };
          // Bail out if nothing actually changed — background polling calls this
          // on an interval; returning `prev` lets React skip the re-render and
          // avoids re-firing the memory-save effect (which would POST every tick).
          return memoryEquivalent(prev, next) ? prev : next;
        });
      }

      // Profile metrics: timestamp-aware merge.
      // • Server has updatedAt AND it's newer than local → server wins (newer device's
      //   values overwrite, so FTP edits on desktop propagate to mobile on next sync).
      // • No server timestamp (old server data) → fill-empty backward-compat merge
      //   so a fresh device still pulls available values without clobbering local data.
      if (data.profileMetrics) {
        const scope = getStorageScope(stored);
        const localUpdatedAt  = localStorage.getItem(storageKey('triHarderProfileMetricsUpdatedAt', scope));
        const serverUpdatedAt = data.profileMetrics.updatedAt;
        const serverIsNewer   = serverUpdatedAt && (!localUpdatedAt || serverUpdatedAt > localUpdatedAt);

        setAthleteProfile(prev => {
          // serverWins: server non-empty value beats local value.
          // fillEmpty:  local non-empty value beats server value (backward compat).
          // Array-aware: schedule.availableDays is an array, and `[] || x` is
          // always `[]` (empty arrays are truthy in JS) — a naive `||` chain
          // would let an empty incoming array silently wipe a populated local
          // value. `pick` treats an empty array the same as an empty string.
          const pick = (primary, secondary) =>
            !isEmptyProfileFieldValue(primary) ? primary
              : (!isEmptyProfileFieldValue(secondary) ? secondary
                : (Array.isArray(primary) || Array.isArray(secondary) ? [] : ''));
          const mergeFn = serverIsNewer
            ? (local = {}, server = {}) =>
                Object.fromEntries(
                  Object.keys({ ...local, ...server }).map(k => [k, pick(server[k], local[k])])
                )
            : (local = {}, server = {}) =>
                Object.fromEntries(
                  Object.keys({ ...local, ...server }).map(k => [k, pick(local[k], server[k])])
                );

          const merged = {
            ...prev,
            personal:   mergeFn(prev.personal,   data.profileMetrics.personal   || {}),
            background: mergeFn(prev.background, data.profileMetrics.background || {}),
            metrics:    mergeFn(prev.metrics,    data.profileMetrics.metrics    || {}),
            goals:      mergeFn(prev.goals,      data.profileMetrics.goals      || {}),
            schedule:   mergeFn(prev.schedule,   data.profileMetrics.schedule   || {}),
            equipment:  mergeFn(prev.equipment,  data.profileMetrics.equipment  || {}),
            health:     mergeFn(prev.health,     data.profileMetrics.health     || {}),
            lifestyle:  mergeFn(prev.lifestyle,  data.profileMetrics.lifestyle  || {}),
          };

          // Persist the enriched profile to localStorage so it survives a refresh.
          // Also persist the winning timestamp so future syncs have a comparison point.
          try {
            localStorage.setItem(storageKey('triHarderProfile', scope), JSON.stringify(merged));
            if (serverIsNewer) {
              localStorage.setItem(storageKey('triHarderProfileMetricsUpdatedAt', scope), serverUpdatedAt);
            }
          } catch { /* localStorage full — in-memory state is still correct */ }
          return merged;
        });
      }

      // Restore the most recent server-saved session into the local history
      // browser if this is a fresh device (or the session isn't already there).
      // Uses a content fingerprint so same-device reloads don't create duplicates.
      if (data.conversation && Array.isArray(data.conversation) && data.conversation.length > 1) {
        const scope = getStorageScope(stored);
        const histKey = storageKey('triHarderConversationHistory', scope);
        try {
          const existing = JSON.parse(localStorage.getItem(histKey) || '[]');
          const userMsgs = data.conversation.filter(m => m.type === 'user');
          const serverFingerprint = JSON.stringify({
            count: data.conversation.length,
            firstUser: userMsgs[0]?.content?.substring(0, 60) || '',
            lastContent: data.conversation[data.conversation.length - 1]?.content?.substring(0, 60) || '',
          });
          const alreadyInHistory = existing.some(s => {
            const u = (s.messages || []).filter(m => m.type === 'user');
            return JSON.stringify({
              count: s.messageCount || s.messages?.length || 0,
              firstUser: u[0]?.content?.substring(0, 60) || '',
              lastContent: s.messages?.slice(-1)[0]?.content?.substring(0, 60) || '',
            }) === serverFingerprint;
          });
          if (!alreadyInHistory) {
            const session = {
              id: 'server-' + Date.now(),
              date: data.conversation[data.conversation.length - 1]?.timestamp || new Date().toISOString(),
              preview: userMsgs[0]?.content?.substring(0, 100) || '…',
              messageCount: data.conversation.length,
              messages: data.conversation,
            };
            const updated = [session, ...existing].slice(0, 20);
            localStorage.setItem(histKey, JSON.stringify(updated));
            setSessionHistory(updated);
          }
        } catch { /* non-critical */ }
      }
    } catch {
      // Server unavailable — local data is the fallback, no action needed
    }
  }, [coachingDataFetch]);

  // ── Live-ish cross-device sync (polling) ─────────────────────────────────
  // syncFromServer already runs once at init. To make a change made on one
  // device (e.g. laptop) show up on another (e.g. phone) without a manual
  // reload, poll it on a modest interval while the tab is visible, and sync
  // immediately whenever the tab regains focus. The server-side memory merge
  // makes this safe (pushes are non-destructive), and syncFromServer's
  // change-guard (memoryEquivalent) means an unchanged poll is a no-op — so a
  // quiet tab just does one cheap, session-token-authenticated GET every 45s
  // with no state churn and no Strava validation.
  useEffect(() => {
    if (showSplash) return undefined;
    if (!userCredentials.isStravaConfigured || !userCredentials.stravaAthleteId) return undefined;

    const POLL_MS = 45000;
    const syncIfVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      syncFromServer();
    };
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') syncFromServer();
    };

    const intervalId = setInterval(syncIfVisible, POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [showSplash, userCredentials.isStravaConfigured, userCredentials.stravaAthleteId, syncFromServer]);

  // After splash dismissal, if the user picked an "intended goal" on the
  // splash chooser, apply it to their athlete profile (fill-empty-only) and
  // queue the welcome modal. Reads the profile from localStorage rather than
  // React state to avoid races with the init() restore.
  useEffect(() => {
    if (showSplash) return;
    const pending = readPendingOnboarding();
    if (!pending) return;

    const scope = getUserScope(userCredentials, isDemoMode);
    if (!scope) return; // wait until creds (or demo) are ready

    // Don't re-seed if we already did so for this scope
    const seededFlag = goalSeededFlagKey(scope);
    if (localStorage.getItem(seededFlag)) {
      clearPendingOnboarding();
      // Still queue welcome (e.g. revisit after partial onboarding) — gated
      // separately by welcomeShownFlag below.
      setPendingWelcomeGoal(pending.goal);
      setPendingWelcome(true);
      return;
    }

    if (isDemoMode) {
      // Demo: in-memory only — never write profile to localStorage so it
      // can't leak into a real account that later signs in on this browser.
      setAthleteProfile(prev => applyGoalSeed(prev, pending.goal));
    } else {
      const key = getProfileStorageKey(userCredentials);
      let stored = null;
      try {
        const raw = localStorage.getItem(key);
        if (raw) stored = JSON.parse(raw);
      } catch {}
      const base = stored || athleteProfile;
      const merged = applyGoalSeed(base, pending.goal);
      setAthleteProfile(merged);
      try { localStorage.setItem(key, JSON.stringify(merged)); } catch {}
    }

    try { localStorage.setItem(seededFlag, '1'); } catch {}
    setPendingWelcomeGoal(pending.goal);
    setPendingWelcome(true);
    clearPendingOnboarding();
  // athleteProfile intentionally omitted — we read from localStorage to
  // avoid stale-state races, and don't want to re-run when profile changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSplash, userCredentials.isStravaConfigured, userCredentials.isIntervalsOAuthConfigured, userCredentials.isConfigured, userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId, userCredentials.athleteId, isDemoMode]);

  // Welcome modal trigger — fires once per scope, after consent banner is
  // resolved and onboarding has been consumed.
  useEffect(() => {
    if (!pendingWelcome) return;
    if (showSplash || showConsentBanner) return;
    const scope = getUserScope(userCredentials, isDemoMode);
    if (!scope) return;

    const flag = welcomeShownFlagKey(scope);
    if (localStorage.getItem(flag)) {
      setPendingWelcome(false);
      return;
    }
    setShowWelcomeModal(true);
  }, [pendingWelcome, showSplash, showConsentBanner, userCredentials, isDemoMode]);

  const dismissWelcomeModal = useCallback(() => {
    const scope = getUserScope(userCredentials, isDemoMode);
    if (scope) {
      try { localStorage.setItem(welcomeShownFlagKey(scope), '1'); } catch {}
    }
    setShowWelcomeModal(false);
    setPendingWelcome(false);
  }, [userCredentials, isDemoMode]);

  // Load privacy preference; show consent banner on first visit
  useEffect(() => {
    const saved = localStorage.getItem('triHarderPrivacy');
    if (saved) {
      try {
        const { mode } = JSON.parse(saved);
        setPrivacyMode(mode || 'full');
      } catch (e) {}
    } else {
      setShowConsentBanner(true);
    }
    const savedStyle = localStorage.getItem('triHarderCoachStyle');
    if (savedStyle) setCoachStyle(savedStyle);
    const savedAnalytics = localStorage.getItem('triHarderAnalyticsOptIn');
    if (savedAnalytics === 'true') setAnalyticsOptIn(true);
  }, []);

  // Check-in greetings — rotate daily so each session feels fresh
  const CHECK_IN_GREETINGS = [
    "How are you feeling today? Let me know what's on your mind — recent sessions, training load, or race prep.",
    "Welcome back! How are the legs feeling? I'm here whenever you're ready to talk training.",
    "Good to see you! How's the training been going? Whether it's a plan, a workout breakdown, or race strategy — let's get into it.",
    "Hey! How are you feeling today — any soreness or fatigue worth flagging before we dive in?",
    "What's on your mind today — training plans, race prep, recovery, or breaking down a recent workout?",
    "How are you doing? Whether you want to analyse a session, plan your week, or talk race strategy, I'm here.",
    "Good to have you back. How have you been feeling in training? Let me know what you'd like to work on.",
  ];

  // Initialize messages — always a fresh check-in each session
  useEffect(() => {
    if (messages.length === 0) {
      const greeting = CHECK_IN_GREETINGS[new Date().getDay() % CHECK_IN_GREETINGS.length];
      setMessages([{
        id: 1,
        type: 'bot',
        content: greeting,
        timestamp: new Date().toLocaleTimeString(),
      }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  // Proactive session opener — fires once per session after workout data loads
  useEffect(() => {
    if (sessionOpenerFiredRef.current) return;
    if (showWelcomeModal || pendingWelcome) return; // wait for onboarding to settle
    if (messages.length !== 1 || messages[0]?.type !== 'bot') return;
    if (workoutData.recentWorkouts.length === 0 || workoutData.isLoading) return;
    if (!userCredentials.isOpenAIConnected || !aiCoach.current) return;

    sessionOpenerFiredRef.current = true;
    // Capture the session ID so we can discard this opener if the user
    // starts a new conversation before the AI call completes.
    const openerSession = sessionCountRef.current;

    // ── New-workout detection ────────────────────────────────────────────────
    // Check if a workout was logged since the athlete last opened the app.
    // Read athlete ID directly from localStorage to avoid adding to the dep array.
    const storedCreds = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
    const athleteId = storedCreds.stravaAthleteId || storedCreds.intervalsAthleteId || 'default';
    const lastSeenKey = `triHarderLastSeenWorkout_${athleteId}`;
    const sortedLatest = [...workoutData.recentWorkouts].sort((a, b) => new Date(b.date) - new Date(a.date));
    const latestWorkout = sortedLatest[0];
    const lastSeenId = localStorage.getItem(lastSeenKey);
    const daysSinceLatest = latestWorkout ? (Date.now() - new Date(latestWorkout.date)) / 86400000 : Infinity;
    const newWorkoutSinceLastSession = !!(
      latestWorkout &&
      String(latestWorkout.id) !== lastSeenId &&
      daysSinceLatest < 3
    );

    const athleteContext = {
      recentWorkouts: recentWorkoutsEnriched.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10),
      athleteProfile,
      trainingSummary: computeTrainingSummary(tssEnrichedWorkouts, athleteGear, gearTags),
      coachingMemory,
      coachStyle,
      athleteZones,
      wellnessData,
      newWorkoutSinceLastSession,
    };

    // Rule-based suggestions computed synchronously — no extra API call
    const suggestions = computeSessionSuggestions(tssEnrichedWorkouts, coachingMemory, athleteProfile.background?.sportFocus);

    setIsTyping(true);
    aiCoach.current.generateSessionOpener(athleteContext)
      .then(opener => {
        // Discard if the user started a new conversation while we were waiting
        if (sessionCountRef.current !== openerSession) return;
        if (opener && messages.length === 1) {
          setMessages(prev => [
            ...prev,
            {
              id: 2,
              type: 'bot',
              content: opener,
              timestamp: new Date().toLocaleTimeString(),
              suggestions: suggestions.length > 0 ? suggestions : undefined,
            }
          ]);
        }
        // Record the latest workout as seen so next session can detect new ones
        if (latestWorkout) {
          localStorage.setItem(lastSeenKey, String(latestWorkout.id));
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsTyping(false);

        // ── ACWR alert ─────────────────────────────────────────────────────
        if (!alertFiredRef.current) {
          const acwrAlert = computeACWRAlert(tssEnrichedWorkouts);
          if (acwrAlert) {
            alertFiredRef.current = true;
            setMessages(prev => [...prev, {
              id: Date.now() + 2,
              type: 'alert',
              level: acwrAlert.level,
              content: acwrAlert.message,
              suggestion: acwrAlert.suggestion,
              timestamp: new Date().toLocaleTimeString(),
            }]);
          }
        }

        // ── Discipline gap nudge ────────────────────────────────────────────
        if (!nudgeFiredRef.current) {
          const gap = computeDisciplineGap(tssEnrichedWorkouts, athleteProfile.background?.sportFocus);
          if (gap) {
            nudgeFiredRef.current = true;
            const label = gap.discipline === 'swim' ? 'swimming' : gap.discipline === 'bike' ? 'cycling' : 'running';
            const daysText = gap.daysSince ? `${gap.daysSince} days` : 'a while';
            setMessages(prev => [...prev, {
              id: Date.now() + 3,
              type: 'nudge',
              discipline: gap.discipline,
              content: `You haven't done a ${label} session in ${daysText} — want to talk about getting back to it?`,
              suggestion: `I haven't done any ${label} in ${daysText} — how should I ease back in?`,
              timestamp: new Date().toLocaleTimeString(),
            }]);
          }
        }

        // ── Memory check-in ────────────────────────────────────────────────
        if (checkinFiredRef.current) return;
        const stale = findStaleTransientCandidate(coachingMemory);
        if (!stale) return;
        checkinFiredRef.current = true;
        setMessages(prev => [
          ...prev,
          {
            id: Date.now() + 1,
            type: 'checkin',
            entryId: stale.id,
            entryText: stale.text,
            content: `Quick check-in — is this still true? "${stale.text}"`,
            timestamp: new Date().toLocaleTimeString(),
          }
        ]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutData.recentWorkouts, workoutData.isLoading, messages.length, userCredentials.isOpenAIConnected]);

  // Daily Coach's Corner briefing — fires once per session when on the Dashboard with data loaded.
  // Cached in sessionStorage (keyed by date + athlete) to avoid re-calling on tab switches.
  // Pass forceRefresh=true to bypass the cache (e.g. when the user clicks Refresh).
  const generateBriefingForDashboard = useCallback(async (forceRefresh = false) => {
    if (!aiCoach.current) return;
    if (workoutData.recentWorkouts.length === 0) return;

    const storedCreds = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
    const athleteId = storedCreds.stravaAthleteId || storedCreds.intervalsAthleteId || 'default';
    const today = new Date().toISOString().slice(0, 10);
    const cacheKey = `triHarderDailyBriefing_v6_${today}_${athleteId}`;

    if (!forceRefresh) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        setCoachBriefing(cached);
        return;
      }
    } else {
      sessionStorage.removeItem(cacheKey);
    }

    setCoachBriefingLoading(true);
    setCoachBriefing(null);

    // Pre-compute flags from full enriched workouts so the briefing prompt
    // gets definitive facts rather than relying on model inference.
    const cutoff7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const strengthCount7d = tssEnrichedWorkouts.filter(
      w => w.type === 'strength' && w.date && new Date(w.date) >= cutoff7d
    ).length;

    // Detect a race completed in the past 7 days. Sources (in priority order):
    //   1. Strava-tagged race activity (workoutType 1 = Run race, 11 = Ride race)
    //   2. A `coachingMemory.races` entry with a date in the past 7 days
    // A race in this window flips the daily briefing into post-race recovery mode.
    let recentRaceCompleted = null;
    const nowMs = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const stravaRace = tssEnrichedWorkouts
      .filter(w => (w.workoutType === 1 || w.workoutType === 11) && w.date)
      .map(w => ({ w, t: new Date(w.date).getTime() }))
      .filter(x => !isNaN(x.t) && (nowMs - x.t) >= 0 && (nowMs - x.t) <= sevenDaysMs)
      .sort((a, b) => b.t - a.t)[0];
    if (stravaRace) {
      recentRaceCompleted = {
        name: stravaRace.w.name || stravaRace.w.title || `${stravaRace.w.type} race`,
        daysAgo: Math.max(0, Math.round((nowMs - stravaRace.t) / 86400000)),
        source: 'strava',
      };
    } else if (Array.isArray(coachingMemory.races)) {
      const memRace = coachingMemory.races
        .filter(r => r.date)
        .map(r => ({ r, t: new Date(r.date).getTime() }))
        .filter(x => !isNaN(x.t) && (nowMs - x.t) >= 0 && (nowMs - x.t) <= sevenDaysMs)
        .sort((a, b) => b.t - a.t)[0];
      if (memRace) {
        recentRaceCompleted = {
          name: memRace.r.name || memRace.r.distance || 'Race',
          daysAgo: Math.max(0, Math.round((nowMs - memRace.t) / 86400000)),
          source: 'memory',
        };
      }
    }

    const athleteContext = {
      recentWorkouts: recentWorkoutsEnriched.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10),
      athleteProfile,
      trainingSummary: computeTrainingSummary(tssEnrichedWorkouts, athleteGear, gearTags),
      coachingMemory,
      coachStyle,
      athleteZones,
      wellnessData,
      flags: {
        hasStrengthGap: strengthCount7d === 0,
        // A completed race should not also drive race-week/taper prompts.
        isRaceWeekOrTaper: !recentRaceCompleted && nextRace != null && nextRace.daysUntil <= 14,
        daysToNextRace: nextRace?.daysUntil ?? null,
        nextRaceName: nextRace?.name ?? null,
        recentRaceCompleted,
      },
    };
    try {
      const briefing = await aiCoach.current.generateDailyBriefing(athleteContext);
      if (briefing) {
        sessionStorage.setItem(cacheKey, briefing);
        setCoachBriefing(briefing);
      } else {
        setCoachBriefing('');
      }
    } catch (_) {
      setCoachBriefing('');
    } finally {
      setCoachBriefingLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutData.recentWorkouts, tssEnrichedWorkouts, recentWorkoutsEnriched, athleteProfile, athleteGear, gearTags, coachingMemory, coachStyle, athleteZones, wellnessData, nextRace]);

  useEffect(() => {
    if (currentPage !== 'dashboard') return;
    if (briefingFiredRef.current) return;
    if (workoutData.isLoading || workoutData.recentWorkouts.length === 0) return;
    if (!userCredentials.isOpenAIConnected) return;
    briefingFiredRef.current = true;
    generateBriefingForDashboard();
  }, [currentPage, workoutData.isLoading, workoutData.recentWorkouts.length, userCredentials.isOpenAIConnected, generateBriefingForDashboard]);

  // Race-week prep briefing — fires once per session when a race is ≤ 7 days away
  // and the opener has already delivered (messages.length >= 2).
  useEffect(() => {
    if (racePrepFiredRef.current) return;
    if (!nextRace || nextRace.daysUntil > 7 || nextRace.daysUntil < 0) return;
    if (messages.length < 2) return; // wait for opener to appear first
    if (!userCredentials.isOpenAIConnected || !aiCoach.current) return;
    if (workoutData.isLoading || workoutData.recentWorkouts.length === 0) return;

    racePrepFiredRef.current = true;
    const raceName = nextRace.name || nextRace.distance || 'Your race';
    const daysUntil = nextRace.daysUntil;
    const athleteContext = {
      recentWorkouts: recentWorkoutsEnriched.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10),
      athleteProfile,
      trainingSummary: computeTrainingSummary(tssEnrichedWorkouts, athleteGear, gearTags),
      coachingMemory,
      coachStyle,
      athleteZones,
      wellnessData,
    };

    aiCoach.current.generateRacePrepBriefing(raceName, daysUntil, athleteContext)
      .then(prep => {
        if (!prep) return;
        setMessages(prev => [...prev, {
          id: Date.now() + 10,
          type: 'race-prep',
          raceName,
          daysUntil,
          content: prep,
          suggestion: `I'm ${daysUntil} days out from ${raceName} — give me a detailed race strategy.`,
          timestamp: new Date().toLocaleTimeString(),
        }]);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextRace, messages.length, workoutData.isLoading, userCredentials.isOpenAIConnected, recentWorkoutsEnriched, tssEnrichedWorkouts, athleteGear, gearTags, athleteProfile, coachingMemory, coachStyle, athleteZones, wellnessData]);

  // Initialize AI coach.
  //
  // Two operating modes:
  //   (1) proxy mode (production default) — calls /api/chat which authenticates the
  //       Bearer token and forwards to Azure OpenAI server-side. API key stays on
  //       the server. Requires *some* connected training account so we have a
  //       Bearer token to send.
  //   (2) direct mode (local dev only) — calls Azure OpenAI directly from the
  //       browser. Triggered when all three of REACT_APP_AZURE_OPENAI_ENDPOINT,
  //       REACT_APP_AZURE_OPENAI_KEY, REACT_APP_AZURE_OPENAI_DEPLOYMENT are set
  //       (typically in a gitignored .env.local). Lets `npm start` exercise the
  //       AI path without running the Azure Functions runtime.
  //
  //       SECURITY NOTE: direct mode embeds the API key in the browser bundle.
  //       It is intended ONLY for local development on a developer machine.
  //       Production builds (via Azure SWA CI/CD) MUST NOT set these env vars —
  //       if they're absent at build time, we fall back to proxy mode.
  useEffect(() => {
    const directEndpoint   = process.env.REACT_APP_AZURE_OPENAI_ENDPOINT;
    const directKey        = process.env.REACT_APP_AZURE_OPENAI_KEY;
    const directDeployment = process.env.REACT_APP_AZURE_OPENAI_DEPLOYMENT;
    const directMode = !!(directEndpoint && directKey && directDeployment);

    const proxyToken = userCredentials.stravaAccessToken
      || userCredentials.intervalsAccessToken
      || userCredentials.apiKey;

    if (directMode) {
      // Direct mode — bypass /api/chat and talk to Azure OpenAI from the browser.
      // Used for local development; never in production.
      // eslint-disable-next-line no-console
      console.info('[AICoach] Initializing in DIRECT mode against', directEndpoint, '/', directDeployment, '— for local dev only.');
      aiCoach.current = new AICoach({
        provider:   'azure',
        endpoint:   directEndpoint,
        apiKey:     directKey,
        deployment: directDeployment,
      });
      setUserCredentials(prev => ({ ...prev, isOpenAIConnected: true }));
      if (athleteProfile.isCompleted && !profileSummary) {
        generateAndSaveProfileSummary(athleteProfile, coachingMemory);
      }
    } else if (proxyToken) {
      aiCoach.current = new AICoach({
        mode: 'proxy',
        proxyUrl: '/api/chat',
        stravaToken: proxyToken
      });
      setUserCredentials(prev => ({ ...prev, isOpenAIConnected: true }));
      // Generate profile summary if profile is complete but no summary exists yet
      if (athleteProfile.isCompleted && !profileSummary) {
        generateAndSaveProfileSummary(athleteProfile, coachingMemory);
      }
    } else {
      aiCoach.current = null;
      setUserCredentials(prev => ({ ...prev, isOpenAIConnected: false }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCredentials.stravaAccessToken, userCredentials.intervalsAccessToken, userCredentials.apiKey]);

  // Keep activeDataSourceRef in sync so loadWorkoutData can read the latest source
  // without capturing it in its dependency array (which would make the callback
  // unstable and trigger the init useEffect to re-run mid-flight).
  useEffect(() => {
    activeDataSourceRef.current = userCredentials.activeDataSource;
  }, [userCredentials.activeDataSource]);

  // Load workout data from active data source.
  // sourceOverride must be a string ('strava' | 'intervals'). If omitted or a
  // non-string value (e.g. a click event), the ref fallback is used. The empty
  // dependency array keeps this callback stable so callers (initializeStravaAPI,
  // initializeIntervalsAPI) do not change identity when credentials are updated,
  // preventing the init useEffect from re-running mid-flight and cancelling itself.
  const loadWorkoutData = useCallback(async (sourceOverride) => {
    const activeSource = typeof sourceOverride === 'string' ? sourceOverride : activeDataSourceRef.current;

    if (activeSource === 'none') {
      console.log('No data source configured');
      return;
    }

    const reqId = ++loadWorkoutReqIdRef.current;
    const isStale = () => reqId !== loadWorkoutReqIdRef.current;

    setWorkoutData(prev => ({ ...prev, isLoading: true }));
    
    try {
      let recentWorkouts = [];
      let plannedWorkouts = [];
      
      if (activeSource === 'intervals' && intervalsAPI.current) {
        console.log('Loading workout data from Intervals.icu...');
        const [intervals, planned, wellness] = await Promise.all([
          intervalsAPI.current.getRecentWorkouts(14),
          intervalsAPI.current.getPlannedWorkouts(),
          intervalsAPI.current.getWellness(14),
        ]);
        recentWorkouts = intervals || [];
        plannedWorkouts = planned || [];
        if (isStale()) return; // a newer load started — don't commit stale data
        setWellnessData(wellness || null);
      } else if (activeSource === 'strava' && stravaAPI.current) {
        console.log('Loading workout data from Strava...');
        const [workouts, gear, zones] = await Promise.all([
          stravaAPI.current.getRecentWorkouts(180),
          stravaAPI.current.getAthleteGear(),
          stravaAPI.current.getAthleteZones(),
        ]);
        const rawWorkouts = workouts || [];
        recentWorkouts = await stravaAPI.current.enrichWithHRZones(rawWorkouts, 5);
        if (isStale()) return; // a newer load started — don't commit stale data
        setAthleteGear(gear || { bikes: [], shoes: [] });
        setAthleteZones(zones || null);
        setWellnessData(null); // wellness is intervals.icu only

        // Prefill empty profile fields from Strava athlete defaults (ftp, weight).
        // Read from localStorage to avoid stale React state in this async closure.
        if (gear?.ftp || gear?.weight) {
          try {
            const creds = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
            const saved = JSON.parse(localStorage.getItem(storageKey('triHarderProfile', getStorageScope(creds))) || '{}');
            const toImport = {};
            if (gear.ftp && !saved.metrics?.ftp) toImport.ftp = gear.ftp;
            if (gear.weight && !saved.personal?.weight) toImport.weight = gear.weight;
            if (Object.keys(toImport).length > 0) {
              setAthleteProfile(prev => ({
                ...prev,
                ...(toImport.ftp !== undefined ? { metrics: { ...prev.metrics, ftp: String(toImport.ftp) } } : {}),
                ...(toImport.weight !== undefined ? { personal: { ...prev.personal, weight: String(toImport.weight) } } : {}),
              }));
              setStravaImportedFields(toImport);
            }
          } catch (e) {
            console.error('Error prefilling profile from Strava defaults:', e);
          }
        }

        plannedWorkouts = [];
      }
      
      console.log('Recent workouts response:', recentWorkouts);
      console.log('Planned workouts response:', plannedWorkouts);

      if (isStale()) return; // a newer load owns the final state
      setWorkoutData({
        recentWorkouts,
        plannedWorkouts,
        isLoading: false
      });
      setDataError('');
      console.log('Workout data updated successfully');

      // Detect new activities since last sync and prompt for RPE if any are unrated
      try {
        const creds = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
        let idsKey = null;
        let ratingsKey = null;
        if (activeSource === 'strava' && creds.stravaAthleteId) {
          idsKey = `triHarderSyncedActivityIds_strava_${creds.stravaAthleteId}`;
          ratingsKey = `triHarderActivityRatings_strava_${creds.stravaAthleteId}`;
        } else if (activeSource === 'intervals' && creds.intervalsAthleteId) {
          idsKey = `triHarderSyncedActivityIds_intervals_${creds.intervalsAthleteId}`;
          ratingsKey = `triHarderActivityRatings_intervals_${creds.intervalsAthleteId}`;
        }
        if (idsKey) {
          const prevIdsRaw = localStorage.getItem(idsKey);
          const prevIds = prevIdsRaw ? new Set(JSON.parse(prevIdsRaw)) : null;
          const currentIds = recentWorkouts.filter(w => w.id).map(w => String(w.id));
          localStorage.setItem(idsKey, JSON.stringify(currentIds));
          if (prevIds && prevIds.size > 0) {
            const currentRatings = JSON.parse(localStorage.getItem(ratingsKey) || '{}');
            const newAndUnrated = recentWorkouts
              .filter(w => w.id && !prevIds.has(String(w.id)))
              .filter(w => !w.feeling && !currentRatings[String(w.id)])
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .slice(0, 5);
            setUnratedNewActivities(newAndUnrated);
          }
          // else: first ever sync — save IDs silently, no prompt
        }
      } catch (e) {
        console.error('RPE detection error:', e);
      }
    } catch (error) {
      console.error('Error loading workout data:', error);
      if (isStale()) return; // a newer load is in charge; don't clobber it
      setWorkoutData(prev => ({ ...prev, isLoading: false }));
      const source = activeSource === 'strava' ? 'Strava'
                   : activeSource === 'intervals' ? 'intervals.icu'
                   : null;
      if (source) {
        setDataError(`Couldn't load your ${source} data. Check your connection, or try reconnecting in Settings.`);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize Intervals API with user credentials
  const initializeIntervalsAPI = useCallback(async (credential, athleteId, useOAuth = false) => {
    try {
      console.log('Initializing Intervals API with user credentials...');
      intervalsAPI.current = new IntervalsAPI(credential, athleteId, useOAuth);
      await loadWorkoutData('intervals');
      console.log('Intervals API initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Intervals API:', error);
      intervalsAPI.current = null;
    }
  }, [loadWorkoutData]);

  // Persistence handler fired by StravaAPI whenever it rotates its tokens
  // internally (e.g. via ensureValidToken inside getRecentWorkouts). Without
  // this, localStorage drifts out of sync from the StravaAPI instance and
  // every subsequent /api/coachingData call 401s because we send a stale
  // refresh token Strava has already invalidated. See CLAUDE.md "Known
  // warnings" for the full incident write-up.
  const persistStravaTokenRotation = useCallback(({ accessToken, refreshToken, expiresAt }) => {
    try {
      const saved = localStorage.getItem('triHarderCredentials');
      if (saved) {
        const creds = JSON.parse(saved);
        const updated = {
          ...creds,
          stravaAccessToken: accessToken,
          stravaRefreshToken: refreshToken,
          stravaExpiresAt: expiresAt,
        };
        localStorage.setItem('triHarderCredentials', JSON.stringify(updated));
      }
    } catch (err) {
      console.error('persistStravaTokenRotation: localStorage write failed:', err);
    }
    setUserCredentials(prev => ({
      ...prev,
      stravaAccessToken: accessToken,
      stravaRefreshToken: refreshToken,
      stravaExpiresAt: expiresAt,
    }));
    if (aiCoach.current) {
      aiCoach.current.stravaToken = accessToken;
    }
  }, []);

  // Initialize Strava API with OAuth tokens.
  // The persistStravaTokenRotation callback is the single mechanism that
  // keeps localStorage / state / aiCoach in sync with the StravaAPI
  // instance — every internal token rotation flows through it automatically.
  // No post-call sync code needed at individual call sites.
  const initializeStravaAPI = useCallback(async (accessToken, refreshToken, expiresAt) => {
    try {
      console.log('Initializing Strava API with OAuth tokens...');
      stravaAPI.current = new StravaAPI(accessToken, persistStravaTokenRotation);
      stravaAPI.current.refreshToken = refreshToken;
      stravaAPI.current.expiresAt = expiresAt;
      await loadWorkoutData('strava');
      console.log('Strava API initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Strava API:', error);
      stravaAPI.current = null;
    }
  }, [loadWorkoutData, persistStravaTokenRotation]);

  // Initialize APIs based on saved credentials
  useEffect(() => {
    if (showSplash) {
      conversationRestoredRef.current = true;
      return undefined;
    }

    let isCancelled = false;
    const init = async () => {
      const savedCredentials = localStorage.getItem('triHarderCredentials');
      if (savedCredentials) {
        try {
          let credentials = JSON.parse(savedCredentials);

        // Refresh intervals.icu OAuth token if expired or expiring within 5 minutes
        if (credentials.intervalsAccessToken && credentials.intervalsExpiresAt) {
          const needsRefresh = Date.now() > (credentials.intervalsExpiresAt - 5 * 60 * 1000);
          if (needsRefresh && credentials.intervalsRefreshToken) {
            const tempIntervals = new IntervalsAPI(credentials.intervalsAccessToken, credentials.intervalsAthleteId, true);
            tempIntervals.refreshToken = credentials.intervalsRefreshToken;
            const result = await tempIntervals.refreshAccessToken();
            if (result.success) {
              credentials = {
                ...credentials,
                intervalsAccessToken: result.accessToken,
                intervalsRefreshToken: result.refreshToken,
                intervalsExpiresAt: result.expiresAt
              };
              localStorage.setItem('triHarderCredentials', JSON.stringify(credentials));
            } else {
              credentials = { ...credentials, intervalsAccessToken: '', isIntervalsOAuthConfigured: false };
              localStorage.setItem('triHarderCredentials', JSON.stringify(credentials));
              setShowSplash(true);
            }
          }
        }

        // Refresh Strava token if expired or expiring within 5 minutes
        if (credentials.stravaAccessToken && credentials.stravaExpiresAt) {
          const needsRefresh = Date.now() > (credentials.stravaExpiresAt - 5 * 60 * 1000);
          if (needsRefresh && credentials.stravaRefreshToken) {
            const tempStrava = new StravaAPI();
            tempStrava.refreshToken = credentials.stravaRefreshToken;
            const result = await tempStrava.refreshAccessToken();
            if (result.success) {
              credentials = {
                ...credentials,
                stravaAccessToken: result.accessToken,
                stravaRefreshToken: result.refreshToken,
                stravaExpiresAt: result.expiresAt
              };
              localStorage.setItem('triHarderCredentials', JSON.stringify(credentials));
            } else {
              // Refresh failed — token is dead, clear Strava and show splash
              credentials = { ...credentials, stravaAccessToken: '', isStravaConfigured: false };
              localStorage.setItem('triHarderCredentials', JSON.stringify(credentials));
              setShowSplash(true);
            }
          }
        }

        const updatedCredentials = {
          apiKey: credentials.apiKey || '',
          athleteId: credentials.athleteId || '',
          openaiApiKey: credentials.openaiApiKey || '',
          aiProvider: credentials.aiProvider || 'openai',
          azureEndpoint: credentials.azureEndpoint || '',
          azureApiVersion: credentials.azureApiVersion || '2025-01-01-preview',
          azureDeployment: credentials.azureDeployment || 'gpt-4o-mini',
          stravaAccessToken: credentials.stravaAccessToken || '',
          stravaRefreshToken: credentials.stravaRefreshToken || '',
          stravaExpiresAt: credentials.stravaExpiresAt || null,
          stravaAthleteId: credentials.stravaAthleteId || '',
          isConfigured: !!(credentials.apiKey && credentials.athleteId),
          isStravaConfigured: !!(credentials.stravaAccessToken),
          isOpenAIConnected: !!(credentials.stravaAccessToken || credentials.intervalsAccessToken || (credentials.apiKey && credentials.athleteId)),
          intervalsAccessToken: credentials.intervalsAccessToken || '',
          intervalsRefreshToken: credentials.intervalsRefreshToken || '',
          intervalsExpiresAt: credentials.intervalsExpiresAt || null,
          intervalsAthleteId: credentials.intervalsAthleteId || '',
          isIntervalsOAuthConfigured: !!(credentials.intervalsAccessToken),
          activeDataSource: credentials.activeDataSource || (
            credentials.intervalsAccessToken ? 'intervals' :
            credentials.apiKey && credentials.athleteId ? 'intervals' :
            credentials.stravaAccessToken ? 'strava' : 'none'
          )
        };

        // Compute the stable storage scope once for all key lookups in init
        const initScope = getStorageScope(credentials);

        // Restore athlete profile from dedicated key
        const savedProfile = localStorage.getItem(storageKey('triHarderProfile', initScope));
        if (savedProfile) {
          try {
            setAthleteProfile(JSON.parse(savedProfile)); 
          } catch (e) {
            console.error('Error restoring profile:', e);
          }
        }
        
        if (credentials.profileSummary) {
          setProfileSummary(credentials.profileSummary);
        }

        setUserCredentials(updatedCredentials);

        // Archive the previous session (if any) and always start fresh
        const conversationKey = storageKey('triHarderConversation', initScope);
        const histKey = storageKey('triHarderConversationHistory', initScope);
        const savedConversation = localStorage.getItem(conversationKey);
        if (savedConversation) {
          try {
            const savedMessages = JSON.parse(savedConversation);
            if (savedMessages.length > 1) {
              const existing = JSON.parse(localStorage.getItem(histKey) || '[]');
              const lastMsgId = savedMessages[savedMessages.length - 1]?.id;
              const alreadyArchived = existing[0]?.messages?.slice(-1)[0]?.id === lastMsgId;
              if (!alreadyArchived) {
                const userMsgs = savedMessages.filter(m => m.type === 'user');
                const session = {
                  id: Date.now(),
                  date: new Date().toISOString(),
                  preview: userMsgs[0]?.content?.substring(0, 100) || '…',
                  messageCount: savedMessages.length,
                  messages: savedMessages,
                };
                const updated = [session, ...existing].slice(0, 20);
                localStorage.setItem(histKey, JSON.stringify(updated));
                setSessionHistory(updated);
              } else {
                setSessionHistory(existing);
              }
            }
          } catch (e) {
            console.error('Error archiving conversation:', e);
          }
        } else {
          // No current session — just load any existing history
          try {
            const existing = JSON.parse(localStorage.getItem(histKey) || '[]');
            if (existing.length) setSessionHistory(existing);
          } catch (e) {}
        }
        conversationRestoredRef.current = true;

        // Restore coaching memory
        const savedMemory = localStorage.getItem(storageKey('triHarderMemory', initScope));
        if (savedMemory) {
          try {
            const parsed = JSON.parse(savedMemory);
            const migrated = reconcileMemory(parsed);
            setCoachingMemory(migrated);
          } catch (e) {}
        }

        // Restore planned workouts from calendar
        const savedWorkouts = localStorage.getItem(storageKey('triHarderPlannedWorkouts', initScope));
        if (savedWorkouts) {
          try { setPlannedWorkouts(JSON.parse(savedWorkouts)); } catch (e) {}
        }

        // Restore training plan — sanitize on load so phases/milestones
        // wiped by a prior buggy AI emit (missing name/dates) get dropped
        // rather than rendering as blank cards forever.
        const savedPlan = localStorage.getItem(storageKey('triHarderTrainingPlan', initScope));
        if (savedPlan) {
          try { setTrainingPlan(sanitizeTrainingPlan(JSON.parse(savedPlan))); } catch (e) {}
        }

        // Restore gear tags
        const savedGearTags = localStorage.getItem(storageKey('triHarderGearTags', initScope));
        if (savedGearTags) {
          try { setGearTags(JSON.parse(savedGearTags)); } catch (e) {}
        }

          if (updatedCredentials.activeDataSource === 'intervals' && updatedCredentials.intervalsAccessToken) {
            await initializeIntervalsAPI(updatedCredentials.intervalsAccessToken, updatedCredentials.intervalsAthleteId, true);
          } else if (updatedCredentials.activeDataSource === 'intervals' && updatedCredentials.apiKey && updatedCredentials.athleteId) {
            await initializeIntervalsAPI(updatedCredentials.apiKey, updatedCredentials.athleteId);
          } else if (updatedCredentials.activeDataSource === 'strava' && updatedCredentials.stravaAccessToken) {
            await initializeStravaAPI(updatedCredentials.stravaAccessToken, updatedCredentials.stravaRefreshToken, updatedCredentials.stravaExpiresAt);
          }

          // Sync from server in background (Strava token required for identity)
          if (updatedCredentials.stravaAccessToken) {
            syncFromServer();
          }
        } catch (error) {
          console.error('Error loading saved credentials:', error);
          conversationRestoredRef.current = true;
        }
      }
    }; // end init
    init().finally(() => {
      if (!isCancelled) {
        setHasInitializedAppData(true);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [showSplash, initializeIntervalsAPI, initializeStravaAPI, syncFromServer]);

  // Keep Azure Functions warm — ping every 4 minutes to prevent cold starts
  useEffect(() => {
    const ping = () => fetch('/api/ping').catch(() => {});
    ping(); // immediate ping on mount
    const id = setInterval(ping, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Handle OAuth redirect from Strava
  useEffect(() => {
    const handleStravaCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      
      if (error) {
        console.error('Strava OAuth error:', error);
        return;
      }
      
      if (code && state) {
        // Idempotency guard: if strava_oauth_state is gone the callback already ran
        // (React re-fired the effect after a state update) — bail silently.
        if (!localStorage.getItem('strava_oauth_state')) return;

        // Clean the URL immediately so effect re-runs triggered by state updates
        // below don't see a ?code= param and attempt to re-process.
        window.history.replaceState({}, document.title, window.location.pathname);

        console.log('Processing Strava OAuth callback...');
        try {
          const tempStravaAPI = new StravaAPI();
          const result = await tempStravaAPI.exchangeCodeForToken(code, state);

          if (result.success) {
            // Save Strava credentials
            const updatedCredentials = {
              ...userCredentials,
              stravaAccessToken: result.accessToken,
              stravaRefreshToken: result.refreshToken,
              stravaExpiresAt: result.expiresAt,
              stravaAthleteId: result.athlete.id.toString(),
              isStravaConfigured: true,
              activeDataSource: userCredentials.activeDataSource !== 'none' ? userCredentials.activeDataSource : 'strava'
            };

            localStorage.setItem('triHarderCredentials', JSON.stringify(updatedCredentials));
            setUserCredentials(updatedCredentials);
            await initializeStravaAPI(result.accessToken, result.refreshToken, result.expiresAt);
            setShowSplash(false);

            // Pull any server-side profile/memory/conversation for this athlete
            // IMMEDIATELY on first connect (parity with the "restore saved
            // credentials" init path below, which already does this). Without
            // this, a brand-new device logging into an existing Strava account
            // showed a blank profile until the next 45s poll tick or tab-focus
            // event — the "profile doesn't sync to a new device" bug (#7).
            syncFromServer();

            // Track Strava authorization (fire-and-forget)
            fetch('/api/trackAuth', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ provider: 'strava', athleteId: result.athlete.id.toString() })
            }).catch(() => {});
          } else {
            console.error('Failed to exchange OAuth code:', result.error);
            if (result.error?.includes('Invalid state')) {
              alert('OAuth session expired — the connection attempt may have been interrupted. Please try connecting Strava again.');
            }
          }
        } catch (error) {
          console.error('Error processing Strava OAuth:', error);
          if (error.message?.includes('Invalid state')) {
            alert('OAuth session expired — the connection attempt may have been interrupted. Please try connecting Strava again.');
          }
        }
      }
    };
    
    handleStravaCallback();
  }, [userCredentials, initializeStravaAPI, syncFromServer]);

  // Handle OAuth redirect from intervals.icu
  useEffect(() => {
    const handleIntervalsCallback = async () => {
      const storedState = localStorage.getItem('intervals_oauth_state');
      if (!storedState) return; // Not an intervals.icu callback

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      if (error) {
        console.error('Intervals.icu OAuth error:', error);
        localStorage.removeItem('intervals_oauth_state');
        window.history.replaceState({}, document.title, window.location.pathname);
        alert('intervals.icu login was cancelled or denied. Please try again.');
        return;
      }

      if (code && state) {
        console.log('Processing intervals.icu OAuth callback...');
        // Remove state immediately so re-runs of this effect don't attempt a second exchange
        localStorage.removeItem('intervals_oauth_state');
        try {
          const tempAPI = new IntervalsAPI(null, null, true);
          const result = await tempAPI.exchangeCodeForToken(code, state);

          if (result.success) {
            const updatedCredentials = {
              ...userCredentials,
              intervalsAccessToken: result.accessToken,
              intervalsRefreshToken: result.refreshToken,
              intervalsExpiresAt: result.expiresAt,
              intervalsAthleteId: result.athleteId ? result.athleteId.toString() : '',
              isIntervalsOAuthConfigured: true,
              activeDataSource: userCredentials.activeDataSource !== 'none' ? userCredentials.activeDataSource : 'intervals'
            };

            localStorage.setItem('triHarderCredentials', JSON.stringify(updatedCredentials));
            setUserCredentials(updatedCredentials);
            await initializeIntervalsAPI(result.accessToken, result.athleteId?.toString() || '', true);
            setShowSplash(false);

            // Track intervals.icu OAuth authorization (fire-and-forget)
            if (result.athleteId) {
              fetch('/api/trackAuth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider: 'intervals-oauth', athleteId: result.athleteId.toString() })
              }).catch(() => {});
            }

            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            console.error('Failed to exchange intervals.icu OAuth code:', result.error);
            window.history.replaceState({}, document.title, window.location.pathname);
            alert(`intervals.icu login failed: ${result.error || 'token exchange error'}. Please try again.`);
          }
        } catch (err) {
          console.error('Error processing intervals.icu OAuth:', err);
          window.history.replaceState({}, document.title, window.location.pathname);
          alert('intervals.icu login failed. Please try again.');
        }
      }
    };

    handleIntervalsCallback();
  }, [userCredentials, initializeIntervalsAPI]);

  // Track one session ping per browser tab (sessionStorage flag = cleared on tab close).
  // Fires only for authenticated OAuth users (Strava or intervals.icu OAuth).
  // Pseudonymised server-side via HMAC; no PII or training data is sent.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('triHarderSessionPinged') === '1') return;

    let provider = null;
    let athleteId = null;
    if (userCredentials.isStravaConfigured && userCredentials.stravaAthleteId) {
      provider = 'strava';
      athleteId = userCredentials.stravaAthleteId;
    } else if (userCredentials.isIntervalsOAuthConfigured && userCredentials.intervalsAthleteId) {
      provider = 'intervals-oauth';
      athleteId = userCredentials.intervalsAthleteId;
    }
    if (!provider || !athleteId) return;

    sessionStorage.setItem('triHarderSessionPinged', '1');
    fetch('/api/trackSession', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, athleteId: String(athleteId) })
    }).catch(() => {});
  }, [
    userCredentials.isStravaConfigured,
    userCredentials.stravaAthleteId,
    userCredentials.isIntervalsOAuthConfigured,
    userCredentials.intervalsAthleteId
  ]);

  // Save Intervals.icu credentials
  const saveCredentials = async (apiKey, athleteId, openaiApiKey = '') => {
    try {
      // Test the Intervals.icu credentials first
      const testAPI = new IntervalsAPI(apiKey, athleteId);
      await testAPI.getRecentWorkouts(1); // Test with just 1 workout
      
      // If successful, save credentials and set as active data source
      const updatedCredentials = {
        ...userCredentials,
        apiKey,
        athleteId,
        openaiApiKey,
        isConfigured: true,
        activeDataSource: 'intervals'
      };
      
      localStorage.setItem('triHarderCredentials', JSON.stringify(updatedCredentials));
      setUserCredentials(updatedCredentials);
      
      // Initialize the Intervals API
      await initializeIntervalsAPI(apiKey, athleteId);
      
      return { success: true };
    } catch (error) {
      console.error('Credential validation failed:', error);
      return { 
        success: false, 
        error: 'Invalid credentials. Please check your API key and Athlete ID.' 
      };
    }
  };

  // Initialize Strava OAuth flow
  const connectStrava = () => {
    const stravaAPI = new StravaAPI();
    stravaAPI.initiateOAuth();
  };

  // Initialize intervals.icu OAuth flow
  const connectIntervalsOAuth = () => {
    const api = new IntervalsAPI(null, null, true);
    api.initiateOAuth();
  };

  // Start demo mode — load mock data and bypass splash
  const startDemoMode = () => {
    setWorkoutData(getMockData());
    setIsDemoMode(true);
    setShowSplash(false);
  };

  const exitDemoMode = () => {
    setIsDemoMode(false);
    setWorkoutData({ recentWorkouts: [], plannedWorkouts: [], isLoading: false });
    setShowSplash(true);
    // Drop any pending onboarding/welcome state so the next user starts clean
    clearPendingOnboarding();
    setPendingWelcome(false);
    setShowWelcomeModal(false);
  };

  // Connect via intervals.icu API key (from splash page)
  const handleConnectApiKey = async (apiKey, athleteId) => {
    const result = await saveCredentials(apiKey, athleteId);
    if (result.success) setShowSplash(false);
    return result;
  };

  // Switch active data source
  const switchDataSource = async (source) => {
    const updatedCredentials = {
      ...userCredentials,
      activeDataSource: source
    };
    
    localStorage.setItem('triHarderCredentials', JSON.stringify(updatedCredentials));
    setUserCredentials(updatedCredentials);
    
    // Initialize the appropriate API
    if (source === 'intervals' && userCredentials.isIntervalsOAuthConfigured) {
      await initializeIntervalsAPI(userCredentials.intervalsAccessToken, userCredentials.intervalsAthleteId, true);
    } else if (source === 'intervals' && userCredentials.isConfigured) {
      await initializeIntervalsAPI(userCredentials.apiKey, userCredentials.athleteId);
    } else if (source === 'strava' && userCredentials.isStravaConfigured) {
      await initializeStravaAPI(userCredentials.stravaAccessToken, userCredentials.stravaRefreshToken, userCredentials.stravaExpiresAt);
    }
  };

  // ── Workout Calendar Handlers ────────────────────────────────────────────
  const handleWorkoutComplete = (workoutId) => {
    setCompletionModal({ open: true, workoutId, rpe: null, note: '' });
  };

  const handleCompletionConfirm = () => {
    const { workoutId, rpe, note } = completionModal;
    setPlannedWorkouts(prev =>
      prev.map(w => w.id === workoutId
        ? { ...w, status: 'completed', rpe: rpe || null, completionNote: note || null }
        : w
      )
    );
    setCompletionModal({ open: false, workoutId: null, rpe: null, note: '' });
  };

  const handleWorkoutSkip = (workoutId) => {
    setPlannedWorkouts(prev =>
      prev.map(w => w.id === workoutId ? { ...w, status: 'skipped' } : w)
    );
  };

  const handleWorkoutEdit = (workoutId, updates) => {
    setPlannedWorkouts(prev =>
      prev.map(w => w.id === workoutId ? { ...w, ...updates } : w)
    );
  };

  const handleWorkoutDelete = (workoutId) => {
    setPlannedWorkouts(prev => prev.filter(w => w.id !== workoutId));
  };

  const handleWorkoutReschedule = (workoutId, newDate) => {
    setPlannedWorkouts(prev =>
      prev.map(w => w.id === workoutId ? { ...w, date: newDate } : w)
    );
  };

  // ── Profile Save Handler ─────────────────────────────────────────────────
  // Returns true on success, false on failure (e.g., quota exceeded) so the
  // ProfilePage save button can show an honest status.
  // TODO (P2): when building athleteContext for AI calls, filter out
  // sport-incompatible metrics (e.g., FTP/CSS for runners) based on
  // background.sportFocus so the model isn't misled by stale values.
  const handleProfileSave = (updatedProfile) => {
    // Diff against the last-known/synced profile BEFORE it's replaced below —
    // this is what lets the server tell an explicit clear apart from a
    // carried-over stale value (Issue #7 follow-up; see computeTouchedProfileFields).
    const touchedFields = computeTouchedProfileFields(athleteProfile, updatedProfile);
    setAthleteProfile(updatedProfile);
    const { stravaAthleteId, intervalsAthleteId, athleteId } = userCredentials;
    const scope = getStorageScope({ stravaAthleteId, intervalsAthleteId, athleteId });
    try {
      localStorage.setItem(storageKey('triHarderProfile', scope), JSON.stringify(updatedProfile));
      console.log('Profile saved:', updatedProfile);
      // Stamp a save timestamp so future syncs can resolve recency correctly.
      const updatedAt = new Date().toISOString();
      localStorage.setItem(storageKey('triHarderProfileMetricsUpdatedAt', scope), updatedAt);
      // Sync metrics + background to server so they're available on other devices.
      // updatedAt travels with the payload so the receiving device can compare.
      // touchedFields tells the server's per-field merge exactly which leaf
      // fields were explicitly edited THIS save (including intentional clears)
      // so it can let them win outright rather than treating a blank value as
      // "never touched, don't overwrite" (the old fill-empty heuristic used to
      // reject intentional clears — see mergeProfileMetricsServer server-side).
      saveToServer({
        profileMetrics: {
          personal:   updatedProfile.personal,
          background: updatedProfile.background,
          metrics:    updatedProfile.metrics,
          goals:      updatedProfile.goals,
          schedule:   updatedProfile.schedule,
          equipment:  updatedProfile.equipment,
          health:     updatedProfile.health,
          lifestyle:  updatedProfile.lifestyle,
          updatedAt,
          touchedFields,
        }
      }).then(res => {
        // The server now merges profileMetrics per-field (Issue #7) instead of
        // overwriting the whole blob, so it can echo back a result that
        // differs from what we just sent (e.g. a section another device
        // edited since our last pull). Adopt that merged result so THIS
        // device converges immediately rather than waiting for the next poll.
        // Only the known profile sections are merged in (mirrors syncFromServer)
        // — the server response's own top-level `updatedAt` is tracked
        // separately, not spread onto athleteProfile's shape.
        const serverProfile = res?.data?.profileMetrics;
        if (!serverProfile) return;
        setAthleteProfile(prev => {
          const merged = {
            ...prev,
            personal:   serverProfile.personal   || prev.personal,
            background: serverProfile.background || prev.background,
            metrics:    serverProfile.metrics    || prev.metrics,
            goals:      serverProfile.goals      || prev.goals,
            schedule:   serverProfile.schedule   || prev.schedule,
            equipment:  serverProfile.equipment  || prev.equipment,
            health:     serverProfile.health     || prev.health,
            lifestyle:  serverProfile.lifestyle  || prev.lifestyle,
          };
          if (JSON.stringify(merged) === JSON.stringify(prev)) return prev;
          try {
            localStorage.setItem(storageKey('triHarderProfile', scope), JSON.stringify(merged));
            localStorage.setItem(storageKey('triHarderProfileMetricsUpdatedAt', scope), serverProfile.updatedAt || updatedAt);
          } catch { /* localStorage full — in-memory state is still correct */ }
          return merged;
        });
      }).catch(() => { /* server unavailable — local save already succeeded */ });
      return true;
    } catch (err) {
      console.error('Profile save failed:', err);
      return false;
    }
  };

  // Clear user credentials for a specific service or all
  const clearCredentials = (service = 'all') => {
    let updatedCredentials = { ...userCredentials };
    
    if (service === 'intervals' || service === 'all') {
      updatedCredentials.apiKey = '';
      updatedCredentials.athleteId = '';
      updatedCredentials.isConfigured = false;
      updatedCredentials.intervalsAccessToken = '';
      updatedCredentials.intervalsRefreshToken = '';
      updatedCredentials.intervalsExpiresAt = null;
      updatedCredentials.intervalsAthleteId = '';
      updatedCredentials.isIntervalsOAuthConfigured = false;
    }
    
    if (service === 'strava' || service === 'all') {
      updatedCredentials.stravaAccessToken = '';
      updatedCredentials.stravaRefreshToken = '';
      updatedCredentials.stravaExpiresAt = null;
      updatedCredentials.stravaAthleteId = '';
      updatedCredentials.isStravaConfigured = false;
      // Invalidate cached server-signed session tokens — they authenticate as
      // this athlete and must not survive a disconnect.
      clearCoachSessions();
    }
    
    if (service === 'all') {
      updatedCredentials.openaiApiKey = '';
      updatedCredentials.aiProvider = 'openai';
      updatedCredentials.azureEndpoint = '';
      updatedCredentials.azureDeployment = 'gpt-4o-mini';
      updatedCredentials.isOpenAIConnected = false;
      updatedCredentials.activeDataSource = 'none';
    } else if (updatedCredentials.activeDataSource === service) {
      // If we're clearing the active data source, switch to the other one or none
      if (service === 'intervals' && updatedCredentials.isStravaConfigured) {
        updatedCredentials.activeDataSource = 'strava';
      } else if (service === 'strava' && (updatedCredentials.isConfigured || updatedCredentials.isIntervalsOAuthConfigured)) {
        updatedCredentials.activeDataSource = 'intervals';
      } else {
        updatedCredentials.activeDataSource = 'none';
      }
    }
    
    localStorage.setItem('triHarderCredentials', JSON.stringify(updatedCredentials));
    setUserCredentials(updatedCredentials);
    
    // Clear API instances
    if (service === 'intervals' || service === 'all') {
      intervalsAPI.current = null;
    }
    if (service === 'strava' || service === 'all') {
      stravaAPI.current = null;
    }
    
    setWorkoutData({
      recentWorkouts: [],
      plannedWorkouts: [],
      isLoading: false
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Only auto-scroll for completed messages; ChatPanel handles scroll during streaming.
  // Scrolling on every streaming chunk update (via smooth scrollIntoView) fights iOS
  // touch gestures and is the primary cause of mobile scroll freeze.
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.streaming) return;
    scrollToBottom();
  }, [messages]);

  // Enhanced AI coach responses using OpenAI + Strava data
  const generateCoachResponse = async (userMessage, onChunk, attachedIds = [], responseMode = null, intent = null) => {
    const message = userMessage.toLowerCase();
    
    // Check for profile completion first
    const profileResponse = handleProfileCompletion(userMessage);
    if (profileResponse) {
      return profileResponse;
    }
    
    // For general coaching questions, use AI with full workout context
    if (aiCoach.current) {
      try {
        const recentWorkouts = recentWorkoutsEnriched
          .slice()
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 20);
        const trainingSummary = computeTrainingSummary(tssEnrichedWorkouts, athleteGear, gearTags);
        const athleteContext = {
          recentWorkouts,
          plannedWorkouts: workoutData.plannedWorkouts.slice(0, 7),
          calendarWorkouts: plannedWorkouts,
          athleteProfile,
          trainingSummary,
          coachingMemory,
          coachStyle,
          trainingPlan,
          athleteZones,
          wellnessData,
          completedPlannedWorkouts: plannedWorkouts
            .filter(w => w.status === 'completed' && (w.rpe || w.completionNote))
            .slice(-7),
        };

        // Inject attached activities for deep analysis. For Strava, fetch detail on demand
        // only for explicitly attached activities so the regular sync stays lightweight.
        if (attachedIds.length > 0) {
          const attachedActivities = workoutData.recentWorkouts
            .filter(w => attachedIds.includes(w.id));

          if (userCredentials.activeDataSource === 'strava' && stravaAPI.current) {
            athleteContext.attachedActivities = await Promise.all(
              attachedActivities.map((workout) => (
                workout.id
                  ? stravaAPI.current.getDetailedWorkout(workout.id, workout)
                  : Promise.resolve(workout)
              ))
            );
          } else {
            athleteContext.attachedActivities = attachedActivities;
          }
        }

        // Ensure a fresh Strava token before calling the AI proxy.
        // Use localStorage as the single source of truth for expiry — every refresh path
        // updates it synchronously.
        try {
          const stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
          const storedExpiresAt = stored.stravaExpiresAt;
          // Refresh if: expiry unknown, already expired, or expiring within 10 minutes
          const tokenNeedsRefresh = !storedExpiresAt || Date.now() > (storedExpiresAt - 10 * 60 * 1000);
          if (tokenNeedsRefresh) {
            const freshToken = await refreshStravaToken();
            if (freshToken) aiCoach.current.stravaToken = freshToken;
          } else if (stored.stravaAccessToken && stored.stravaAccessToken !== aiCoach.current.stravaToken) {
            // localStorage has a fresher token (e.g. refreshed during activity load) — sync it
            aiCoach.current.stravaToken = stored.stravaAccessToken;
          }
        } catch {
          // Fall through — 401 handler below will catch it if the token is truly dead
        }

        let aiResponse;
        try {
          if (onChunk) {
            aiResponse = await aiCoach.current.generateResponseStream(userMessage, athleteContext, messages, onChunk, responseMode, intent);
          } else {
            aiResponse = await aiCoach.current.generateResponse(userMessage, athleteContext, messages, responseMode, intent);
          }
        } catch (aiError) {
          // 401 = Strava token expired mid-session — refresh and retry once
          if (aiError.status === 401) {
            const newToken = await refreshStravaToken();
            if (newToken) {
              aiResponse = onChunk
                ? await aiCoach.current.generateResponseStream(userMessage, athleteContext, messages, onChunk, responseMode, intent)
                : await aiCoach.current.generateResponse(userMessage, athleteContext, messages, responseMode, intent);
            } else {
              // Token refresh failed — session expired
              setShowSplash(true);
              return "Your session has expired. Please reconnect via the splash screen.";
            }
          } else {
            throw aiError;
          }
        }

        // Background: extract and persist any new coaching facts (non-blocking).
        // After merge, also auto-compact if the notes list has grown too large.
        aiCoach.current.extractMemoryUpdate(userMessage, aiResponse, coachingMemory)
          .then(updates => {
            if (!updates) return;
            setCoachingMemory(prev => {
              const merged = mergeMemory(prev, updates);
              if (shouldCompact(merged) && aiCoach.current?.compactMemoryNotes) {
                // Fire-and-forget compaction; result applied in a follow-up setState.
                aiCoach.current.compactMemoryNotes(merged.noteEntries)
                  .then(compacted => {
                    if (!Array.isArray(compacted) || compacted.length === 0) return;
                    setCoachingMemory(cur => applyCompaction(cur, compacted));
                  })
                  .catch(() => {});
              }
              return merged;
            });
          })
          .catch(() => {});

        return aiResponse;
      } catch (error) {
        console.error('AI error:', error);
        if (error.status === 500) {
          return `Sorry, the AI service is temporarily unavailable — the server may be warming up. Please try again in a moment.`;
        }
        return generateFallbackResponse(message);
      }
    } else {
      return generateFallbackResponse(message);
    }
  };

  // Fallback responses when OpenAI is not available
  const generateFallbackResponse = (message) => {
    // Training plan specific responses
    if (message.includes('training plan') || message.includes('create plan') || message.includes('plan for')) {
      const raceInfo = athleteProfile.races || 'your upcoming race';
      const weeklyHours = athleteProfile.weeklyHours || '8-12 hours';
      
      return `I'd love to help you create a training plan for ${raceInfo}! Here's what I need to build a **personalized periodized plan**:

📅 **Race Details Needed:**
- Race date and distance (Sprint/Olympic/Half/Full Ironman)
- Current fitness level and recent training history
- Time available per week (you mentioned: ${weeklyHours})

🏗️ **My Training Plan Structure:**
I'll create a **periodized plan** with these phases:
- **Base Phase**: Aerobic development, technique work
- **Build Phase**: Add intensity and race-specific workouts  
- **Peak Phase**: Race simulation and sharpness
- **Taper Phase**: Volume reduction while maintaining fitness

📊 **Weekly Format** (example for Olympic distance):
| Week | Phase | Hours | Swim Focus | Bike Focus | Run Focus |
|------|-------|-------|------------|------------|-----------|
| 1-4  | Base  | 8-10h | Technique  | Z2 rides   | Easy runs |
| 5-8  | Build | 10-12h| Threshold  | Intervals  | Tempo runs|

Would you like me to create a specific plan? Just tell me your **race date** and **distance**!`;
    }
    
    // Profile building responses
    if (message.includes('beginner') || message.includes('new to triathlon')) {
      return "Great to meet a new triathlete! Starting your triathlon journey is exciting. Let's build a foundation focusing on **technique** and **gradual fitness building**. How many hours per week can you realistically dedicate to training?";
    }
    
    if (message.includes('tired') || message.includes('fatigue')) {
      return "I hear you're feeling tired. This could indicate you need more **recovery**. Let me adjust your upcoming workouts to include more easy sessions. How has your **sleep** been lately? Also, I'll review your recent training load from Strava and suggest modifications.";
    }
    
    if (message.includes('swim') && (message.includes('struggle') || message.includes('difficult'))) {
      return "Swimming challenges are very common! Let's focus on **technique first**. I'll create some drill-focused sessions for you. Have you considered getting stroke analysis? In the meantime, I'll adjust your swim workouts to emphasize *form over speed*.";
    }
    
    if (message.includes('race') || message.includes('competition')) {
      return "Exciting! Tell me about your target race - **what distance** and **when**? This will help me create a periodized plan. I'll also need to know your current fitness baseline to structure the training phases properly.";
    }
    
    if (message.includes('nutrition') || message.includes('fuel')) {
      return "Nutrition is crucial for triathlon performance! For training sessions over **90 minutes**, aim for **30-60g carbs per hour**. For races, practice your nutrition strategy during long training sessions. What's your current approach to fueling during workouts?";
    }
    
    if (message.includes('time') || message.includes('busy') || message.includes('schedule')) {
      return "Time management is key for busy athletes! Let's prioritize your most important sessions. I can create a flexible plan with **'non-negotiable'** key workouts and *optional* sessions. How many days per week can you commit to training?";
    }
    
    if (message.includes('workout') || message.includes('training')) {
      return "I can analyze your **activity data** to provide specific feedback on your **power/pace trends**, **training load**, and **recovery metrics**. Have you set up your training zones yet? Connect a training account in Settings if you haven't already.";
    }
    
    // Default encouraging response
    return "Thanks for sharing that with me! I'm analyzing your input to provide the best coaching guidance. Remember, **consistency beats perfection** in triathlon training. What's your biggest challenge right now that I can help you with?";
  };

  const generateAndSaveProfileSummary = async (profile, memory) => {
    if (!aiCoach.current || !profile.isCompleted) return;
    try {
      const summary = await aiCoach.current.generateProfileSummary(profile, memory);
      setProfileSummary(summary);
      // Persist alongside the profile
      const saved = localStorage.getItem('triHarderCredentials');
      if (saved) {
        const creds = JSON.parse(saved);
        localStorage.setItem('triHarderCredentials', JSON.stringify({ ...creds, profileSummary: summary }));
      }
    } catch (e) {
      console.error('Profile summary generation failed:', e);
    }
  };

  const handleConsentChoice = (mode) => {
    localStorage.setItem('triHarderPrivacy', JSON.stringify({ mode, consentDate: new Date().toISOString() }));
    setPrivacyMode(mode);
    setShowConsentBanner(false);
  };

  // Toggle opt-in chat analytics logging. Persists the choice (and consent date)
  // so it survives reloads; the logging effect reads the `analyticsOptIn` state.
  const handleAnalyticsOptInChange = (enabled) => {
    localStorage.setItem('triHarderAnalyticsOptIn', enabled ? 'true' : 'false');
    if (enabled) {
      localStorage.setItem('triHarderAnalyticsConsent', JSON.stringify({ consentDate: new Date().toISOString() }));
    }
    setAnalyticsOptIn(enabled);
  };

  const mergeMemory = (existing, updates) => {
    const merged = { ...existing };
    // Stamp newly (re-)learned structured facts with an effective timestamp so
    // a fact the athlete deleted then mentioned again survives its tombstone
    // (memoryUpdatedAt >= deletedAt), while old/stale copies without a stamp
    // stay suppressed. See src/utils/coachingMemory.js applyFactTombstones.
    //
    // Every update whose key matches an EXISTING race/injury also gets
    // re-stamped (merging in any updated fields) — not just brand-new
    // entries. Without this, a stale device re-mentioning a fact the athlete
    // deleted elsewhere would match an existing key, be treated as "already
    // known" and never restamped, and the server would still see the
    // pre-deletion timestamp and suppress the genuine re-learn. Untouched
    // existing entries (no matching update) are carried through unstamped.
    // See mergeStructuredFactUpdates in utils/coachingMemory.js.
    const nowIso = new Date().toISOString();
    if (updates.races?.length > 0) {
      merged.races = mergeStructuredFactUpdates(existing.races, updates.races, raceKey, nowIso);
    }
    if (updates.injuries?.length > 0) {
      merged.injuries = mergeStructuredFactUpdates(existing.injuries, updates.injuries, injuryKey, nowIso);
    }
    if (updates.fitnessLevel) merged.fitnessLevel = updates.fitnessLevel;
    if (updates.goals) merged.goals = updates.goals;
    if (updates.preferences) merged.preferences = updates.preferences;
    if (updates.strengthPattern) merged.strengthPattern = updates.strengthPattern;
    // Notes are now structured entries with kind + timestamps; dedupe by text.
    if (Array.isArray(updates.notes) && updates.notes.length > 0) {
      merged.noteEntries = mergeNoteEntries(existing.noteEntries || [], updates.notes);
    } else if (typeof updates.notes === 'string' && updates.notes.trim()) {
      // Back-compat: legacy string notes — treat as a single transient entry.
      merged.noteEntries = mergeNoteEntries(existing.noteEntries || [], [{ text: updates.notes.trim(), kind: 'transient' }]);
    }
    merged.lastUpdated = nowIso;
    // Run the race lifecycle sanitizer so a newly-extracted race gets a
    // status, and any that have aged into archive territory are moved to
    // pastRaces. Idempotent — safe if updates contained no races.
    const sanitized = sanitizeRaces(merged);
    merged.races = sanitized.races;
    merged.pastRaces = sanitized.pastRaces;
    return reconcileMemory(merged);
  };

  // Resolve a memory check-in by removing the prompt message and applying the choice.
  const resolveCheckin = useCallback((entryId, action) => {
    const nowIso = new Date().toISOString();
    setMessages(prev => prev.filter(m => !(m.type === 'checkin' && m.entryId === entryId)));
    setCoachingMemory(prev => {
      const entries = prev.noteEntries || [];
      let nextEntries;
      let deletedNotes = prev.deletedNotes || [];
      if (action === 'drop') {
        const dropped = entries.find(e => e.id === entryId);
        nextEntries = entries.filter(e => e.id !== entryId);
        // Tombstone so the drop survives server union merges and stale devices.
        if (dropped) deletedNotes = addTombstone(deletedNotes, dropped.text, nowIso);
      } else if (action === 'confirm') {
        nextEntries = entries.map(e => e.id === entryId ? { ...e, lastConfirmedAt: nowIso, deferredUntil: null } : e);
      } else { // skip — defer to tomorrow so it can resurface in a later session
        const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        nextEntries = entries.map(e => e.id === entryId ? { ...e, deferredUntil: tomorrow } : e);
      }
      return { ...prev, noteEntries: nextEntries, deletedNotes, lastUpdated: nowIso };
    });
  }, []);

  // Delete a durable/transient memory note. Records a tombstone so the deletion
  // propagates through server union merges and can't be resurrected by a stale
  // device that still holds the note.
  const deleteMemoryNote = useCallback((entriesToDelete) => {
    const requestedEntries = (Array.isArray(entriesToDelete) ? entriesToDelete : [entriesToDelete]).filter(Boolean);
    if (requestedEntries.length === 0) return;
    const nowIso = new Date().toISOString();
    setCoachingMemory(prev => {
      const entries = prev.noteEntries || [];
      const requestedTargets = requestedEntries.map(entry =>
        entry.id ? entries.find(e => e.id === entry.id) : entries.find(e => e.text === entry.text)
      );
      const targets = requestedTargets.filter(Boolean);
      const targetTexts = [...new Set(requestedEntries.map((entry, index) => (requestedTargets[index] && requestedTargets[index].text) || entry.text).filter(Boolean))];
      const nextEntries = entries.filter(e => !targets.includes(e) && !requestedEntries.some(requested => !requested.id && requested.text === e.text));
      const deletedNotes = targetTexts.reduce(
        (tombstones, text) => addTombstone(tombstones, text, nowIso),
        prev.deletedNotes || []
      );
      return { ...prev, noteEntries: nextEntries, deletedNotes, lastUpdated: nowIso };
    });
  }, []);

  // Delete a target/past race from coaching memory. Records a structured-fact
  // tombstone (keyed by raceKey) so the deletion survives the server's array
  // union merge and can't be resurrected by a stale device or a save-echo.
  // Removes from BOTH races and pastRaces so archive-location can't hide a copy.
  const deleteRace = useCallback((race) => {
    if (!race) return;
    const nowIso = new Date().toISOString();
    const key = raceKey(race);
    setCoachingMemory(prev => {
      const races = (prev.races || []).filter(r => raceKey(r) !== key);
      const pastRaces = (prev.pastRaces || []).filter(r => raceKey(r) !== key);
      const deletedFacts = addFactTombstone(prev.deletedFacts || [], 'race', key, nowIso);
      return { ...prev, races, pastRaces, deletedFacts, lastUpdated: nowIso };
    });
  }, []);

  // Delete an injury from coaching memory. Records a structured-fact tombstone
  // (keyed by normalized description) so the deletion survives union merges.
  const deleteInjury = useCallback((injury) => {
    if (!injury) return;
    const nowIso = new Date().toISOString();
    const key = injuryKey(injury);
    setCoachingMemory(prev => {
      const injuries = (prev.injuries || []).filter(i => injuryKey(i) !== key);
      const deletedFacts = addFactTombstone(prev.deletedFacts || [], 'injury', key, nowIso);
      return { ...prev, injuries, deletedFacts, lastUpdated: nowIso };
    });
  }, []);

  // Wipe coaching memory durably. Mints a tombstone for EVERY currently-known
  // note, race, pastRace, and injury so the wipe survives the server union and
  // stale devices, preserves existing tombstones, clears scalar fields, and
  // stamps a real `lastUpdated` so the persistence effect actually POSTs it
  // (the old code set lastUpdated=null and the save effect bailed early, so a
  // later sync silently restored everything). When `clearConversations` is set,
  // the current chat and the conversation localStorage key are cleared too.
  const clearCoachingMemory = useCallback(({ clearConversations = false } = {}) => {
    const nowIso = new Date().toISOString();
    setCoachingMemory(prev => {
      const { deletedNotes, deletedFacts } = buildClearMemoryTombstones(prev, nowIso);
      return { ...EMPTY_MEMORY, deletedNotes, deletedFacts, lastUpdated: nowIso };
    });
    if (clearConversations) {
      const { stravaAthleteId, intervalsAthleteId, athleteId } = userCredentials;
      const scope = getStorageScope({ stravaAthleteId, intervalsAthleteId, athleteId });
      localStorage.removeItem(storageKey('triHarderConversation', scope));
      setMessages([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCredentials.stravaAthleteId, userCredentials.intervalsAthleteId, userCredentials.athleteId]);

  // Manually compact coaching memory notes via AI. Returns a result string for toast UI.
  const compactMemoryNow = useCallback(async () => {
    if (!aiCoach.current?.compactMemoryNotes) {
      return { ok: false, message: 'AI coach is not connected.' };
    }
    const entries = coachingMemory.noteEntries || [];
    if (entries.length === 0) {
      return { ok: false, message: 'No notes to compact.' };
    }
    try {
      const compacted = await aiCoach.current.compactMemoryNotes(entries);
      if (!Array.isArray(compacted) || compacted.length === 0) {
        return { ok: false, message: 'Compaction returned no entries — keeping originals.' };
      }
      // Compute the result deterministically from the SAME snapshot `entries`
      // came from, then commit it. Reading a count out of the setState updater
      // is unreliable — the updater runs during render (not at call time) and is
      // double-invoked under StrictMode, so `removed` could be computed against a
      // stale value and show the wrong count ("Compact now appears ineffective").
      const next = applyCompaction(coachingMemory, compacted);
      const appliedLen = next.noteEntries.length;
      setCoachingMemory(next);
      const removed = entries.length - appliedLen;
      return { ok: true, message: removed > 0 ? `Compacted to ${appliedLen} entries (removed ${removed}).` : `Notes are already concise (${appliedLen} entries).` };
    } catch (e) {
      return { ok: false, message: 'Compaction failed — please try again.' };
    }
  }, [coachingMemory]);

  const archiveCurrentSession = useCallback(() => {
    if (messages.length <= 1) return; // nothing beyond the greeting
    const histKey = userCredentials.stravaAthleteId
      ? `triHarderConversationHistory_${userCredentials.stravaAthleteId}`
      : 'triHarderConversationHistory';
    const existing = JSON.parse(localStorage.getItem(histKey) || '[]');
    const lastMsgId = messages[messages.length - 1]?.id;
    if (existing[0]?.messages?.slice(-1)[0]?.id === lastMsgId) return; // already archived
    const userMsgs = messages.filter(m => m.type === 'user');
    const session = {
      id: Date.now(),
      date: new Date().toISOString(),
      preview: userMsgs[0]?.content?.substring(0, 100) || '…',
      messageCount: messages.length,
      messages: [...messages],
    };
    const updated = [session, ...existing].slice(0, 20);
    localStorage.setItem(histKey, JSON.stringify(updated));
    setSessionHistory(updated);
  }, [messages, userCredentials.stravaAthleteId]);

  const startNewConversation = () => {
    // Increment so any in-flight opener/alert callbacks discard their results
    sessionCountRef.current += 1;
    sessionOpenerFiredRef.current = false;
    checkinFiredRef.current = false;
    alertFiredRef.current = false;
    nudgeFiredRef.current = false;
    racePrepFiredRef.current = false;
    archiveCurrentSession();
    const { stravaAthleteId, intervalsAthleteId, athleteId } = userCredentials;
    const scope = getStorageScope({ stravaAthleteId, intervalsAthleteId, athleteId });
    localStorage.removeItem(storageKey('triHarderConversation', scope));
    setMessages([]);
    // New session → new analytics row (opt-in logging only).
    analyticsSessionIdRef.current = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  };

  // ── Coach Action proposals (Phase 1a) ────────────────────────────────────
  // applyProposal mutates Calendar / Plan state based on a confirmed proposal,
  // with precondition validation against the latest state. Returns a result
  // object the caller writes back onto the message's proposal entry to drive
  // the ProposalCard's status banner (confirmed / stale).
  //
  // - LLM-supplied IDs are NEVER trusted: workout IDs are generated here at
  //   apply time, not at parse time.
  // - add_workouts dedupes by date+sport+title+duration against current
  //   plannedWorkouts so re-asking "what's my week?" can't double up.
  // - remove_* / modify_* check the target still exists; missing targets
  //   resolve as `stale` with a user-readable reason (never silent mutation).
  const applyProposal = useCallback((proposal) => {
    // Diagnostic log — shows what's coming in and the current state it'll be
    // applied against. Pair with the UI's stale-reason banner to triage why a
    // proposal landed as 'stale' vs 'confirmed'. Particularly useful for plan
    // actions where preconditions (existing phaseId etc.) often fail silently
    // from the user's perspective.
    console.log('[CoachActions] applyProposal:', {
      type: proposal?.type,
      proposalId: proposal?.proposalId,
      summary: proposal?.summary,
      payload: proposal?.payload,
      contextSnapshot: {
        currentPhases: trainingPlan?.phases?.length || 0,
        currentMilestones: trainingPlan?.milestones?.length || 0,
        currentPlannedWorkouts: plannedWorkouts?.length || 0,
        firstPhaseId: trainingPlan?.phases?.[0]?.id || null,
      },
    });
    if (!proposal || !proposal.type) return { status: 'stale', staleReason: 'Invalid proposal' };

    switch (proposal.type) {
      case 'add_workouts': {
        const items = proposal.payload?.items || [];
        const keyOf = (w) => `${(w.date || '').slice(0, 10)}|${w.sport}|${(w.title || '').toLowerCase().trim()}|${w.duration}`;
        // First pass: dedupe within the proposal itself so a single proposal
        // listing the same workout twice doesn't double up.
        const seen = new Set();
        const uniqueItems = items.filter((item) => {
          const k = keyOf(item);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        // Second pass happens inside the functional updater so a rapid
        // double-confirm can't race past the stale closure of plannedWorkouts.
        let actuallyAdded = 0;
        setPlannedWorkouts((prev) => {
          const existingKeys = new Set(prev.map((w) => keyOf({
            date: typeof w.date === 'string' ? w.date : new Date(w.date).toISOString(),
            sport: w.sport,
            title: w.title,
            duration: w.duration,
          })));
          const ts = Date.now();
          const toAdd = uniqueItems
            .filter((item) => !existingKeys.has(keyOf(item)))
            .map((item, i) => ({
              id: `workout_${ts}_${i}_${Math.random().toString(36).slice(2, 9)}`,
              date: new Date(item.date).toISOString(),
              sport: item.sport,
              title: item.title,
              duration: item.duration,
              zone: item.zone || '',
              description: item.description || '',
              status: 'planned',
              createdBy: 'ai',
            }));
          actuallyAdded = toAdd.length;
          return toAdd.length === 0 ? prev : [...prev, ...toAdd];
        });
        if (actuallyAdded === 0) {
          return { status: 'stale', staleReason: 'These workouts already exist on your calendar' };
        }
        return {
          status: 'confirmed',
          applied: { count: actuallyAdded, skipped: items.length - actuallyAdded },
        };
      }

      case 'remove_workouts': {
        const ids = proposal.payload?.workoutIds || [];
        const existingIds = new Set(plannedWorkouts.map((w) => w.id));
        const real = ids.filter((id) => existingIds.has(id));
        if (real.length === 0) {
          return { status: 'stale', staleReason: 'These workouts were already removed' };
        }
        const realSet = new Set(real);
        setPlannedWorkouts((prev) => prev.filter((w) => !realSet.has(w.id)));
        return {
          status: 'confirmed',
          applied: { count: real.length, skipped: ids.length - real.length },
        };
      }

      case 'modify_phase': {
        const { phaseId, changes } = proposal.payload || {};
        const existing = trainingPlan.phases.find((p) => p.id === phaseId);
        if (!existing) {
          return { status: 'stale', staleReason: 'That training phase no longer exists' };
        }
        const candidate = decoratePhase({ ...existing, ...changes });
        if (candidate.startDate && candidate.endDate && candidate.startDate > candidate.endDate) {
          return { status: 'stale', staleReason: 'Those dates would put the phase end before its start' };
        }
        setTrainingPlan((prev) => ({
          ...prev,
          phases: prev.phases.map((p) => (p.id === phaseId ? candidate : p)),
        }));
        return { status: 'confirmed', applied: { count: 1 } };
      }

      case 'remove_phase': {
        const { phaseId } = proposal.payload || {};
        if (!trainingPlan.phases.some((p) => p.id === phaseId)) {
          return { status: 'stale', staleReason: 'That training phase was already removed' };
        }
        setTrainingPlan((prev) => ({ ...prev, phases: prev.phases.filter((p) => p.id !== phaseId) }));
        return { status: 'confirmed', applied: { count: 1 } };
      }

      case 'add_phase': {
        const ts = Date.now();
        const candidate = decoratePhase({
          ...proposal.payload.phase,
          id: `phase_${ts}_${Math.random().toString(36).slice(2, 9)}`,
        });
        if (candidate.startDate && candidate.endDate && candidate.startDate > candidate.endDate) {
          return { status: 'stale', staleReason: 'Those dates would put the phase end before its start' };
        }
        setTrainingPlan((prev) => ({
          ...prev,
          phases: [...(prev.phases || []), candidate],
        }));
        return { status: 'confirmed', applied: { count: 1 } };
      }

      case 'add_milestone': {
        const ts = Date.now();
        const m = decorateMilestone({
          ...proposal.payload.milestone,
          id: `milestone_${ts}_${Math.random().toString(36).slice(2, 9)}`,
        });
        setTrainingPlan((prev) => ({ ...prev, milestones: [...prev.milestones, m] }));
        return { status: 'confirmed', applied: { count: 1 } };
      }

      case 'remove_milestone': {
        const { milestoneId } = proposal.payload || {};
        if (!trainingPlan.milestones.some((m) => m.id === milestoneId)) {
          return { status: 'stale', staleReason: 'That milestone was already removed' };
        }
        setTrainingPlan((prev) => ({
          ...prev,
          milestones: prev.milestones.filter((m) => m.id !== milestoneId),
        }));
        return { status: 'confirmed', applied: { count: 1 } };
      }

      default:
        return { status: 'stale', staleReason: `Unknown action: ${proposal.type}` };
    }
  }, [plannedWorkouts, trainingPlan]);

  // Wire ProposalCard buttons → apply or dismiss. Refuses if the message is
  // no longer in the active conversation (e.g. session was cleared).
  const handleConfirmProposal = useCallback((messageId, proposal) => {
    if (!proposal || proposal.status !== 'pending') return;
    const result = applyProposal(proposal);
    setMessages((prev) => prev.map((m) =>
      m.id === messageId && m.proposals
        ? {
            ...m,
            proposals: m.proposals.map((p) =>
              p.proposalId === proposal.proposalId ? { ...p, ...result } : p
            ),
          }
        : m
    ));
    const _stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
    if (_stored.stravaAthleteId) {
      postCoachActionsTelemetry({
        event: 'outcome',
        athleteId: _stored.stravaAthleteId,
        status: result.status,
        proposalType: proposal.type,
      });
    }
  }, [applyProposal]);

  const handleConfirmAllProposals = useCallback((messageId, proposals) => {
    const pending = (proposals || []).filter(p => p.status === 'pending');
    if (pending.length === 0) return;
    // Apply each proposal and collect results, then batch a single state update.
    const results = pending.map(p => ({ proposalId: p.proposalId, result: applyProposal(p) }));
    setMessages((prev) => prev.map((m) => {
      if (m.id !== messageId || !m.proposals) return m;
      return {
        ...m,
        proposals: m.proposals.map((p) => {
          const found = results.find(r => r.proposalId === p.proposalId);
          return found ? { ...p, ...found.result } : p;
        }),
      };
    }));
    const _stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
    if (_stored.stravaAthleteId) {
      results.forEach(({ result }, i) => {
        postCoachActionsTelemetry({
          event: 'outcome',
          athleteId: _stored.stravaAthleteId,
          status: result.status,
          proposalType: pending[i]?.type,
        });
      });
    }
  }, [applyProposal]);

  const handleDiscardProposal = useCallback((messageId, proposal) => {
    const proposalId = proposal?.proposalId ?? proposal;
    setMessages((prev) => prev.map((m) =>
      m.id === messageId && m.proposals
        ? {
            ...m,
            proposals: m.proposals.map((p) =>
              p.proposalId === proposalId && p.status === 'pending'
                ? { ...p, status: 'dismissed' }
                : p
            ),
          }
        : m
    ));
    const _stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
    if (_stored.stravaAthleteId) {
      postCoachActionsTelemetry({
        event: 'outcome',
        athleteId: _stored.stravaAthleteId,
        status: 'dismissed',
        proposalType: proposal?.type || null,
      });
    }
  }, []);

  // Structured-output path handler (Commit B; extended C3).
  //
  // Mirrors the context-building of generateCoachResponse but calls the
  // structured AI method, parses the resulting JSON envelope, and
  // attaches proposals directly. No streaming — the response IS the
  // envelope, not incremental prose.
  //
  // `methodName` selects which AICoach method to call. Two variants:
  //   - 'generateStructuredResponse' (default) — full envelope schema,
  //     used for tactical add/remove/modify requests.
  //   - 'generatePhaseSkeleton' — narrow envelope schema, only
  //     add_phase/modify_phase/remove_phase/add_milestone/remove_milestone
  //     allowed. Used for plan-build requests so the AI can't shortcut
  //     into "dump everything as add_workouts" (the recurring failure
  //     mode that motivated this split).
  //
  // Returns true if the message was handled (the AI responded, OR an
  // expected error was surfaced to the user). Returns false to indicate
  // the caller should fall back to the prose path (rare — only if the
  // structured path is unavailable for a non-error reason). Throws on
  // unexpected errors; caller catches and falls back.
  const handleStructuredSend = useCallback(async (userMessage, attachedIds, botMsgId, sendSession, methodName = 'generateStructuredResponse', intent = null) => {
    if (!aiCoach.current || typeof aiCoach.current[methodName] !== 'function') {
      return false;  // AICoach missing the requested method — fall back
    }

    // Build athlete context — same shape as generateCoachResponse.
    const recentWorkouts = recentWorkoutsEnriched
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 20);
    const trainingSummary = computeTrainingSummary(tssEnrichedWorkouts, athleteGear, gearTags);
    const athleteContext = {
      recentWorkouts,
      plannedWorkouts: workoutData.plannedWorkouts.slice(0, 7),
      calendarWorkouts: plannedWorkouts,
      athleteProfile,
      trainingSummary,
      coachingMemory,
      coachStyle,
      trainingPlan,
      athleteZones,
      wellnessData,
      completedPlannedWorkouts: plannedWorkouts
        .filter(w => w.status === 'completed' && (w.rpe || w.completionNote))
        .slice(-7),
    };

    if (attachedIds && attachedIds.length > 0) {
      const attached = workoutData.recentWorkouts.filter(w => attachedIds.includes(w.id));
      if (userCredentials.activeDataSource === 'strava' && stravaAPI.current) {
        athleteContext.attachedActivities = await Promise.all(
          attached.map(w => (w.id ? stravaAPI.current.getDetailedWorkout(w.id, w) : Promise.resolve(w)))
        );
      } else {
        athleteContext.attachedActivities = attached;
      }
    }

    // Ensure a fresh Strava token before the AI call (same logic as
    // generateCoachResponse — the proxy needs a valid token to auth).
    try {
      const stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
      const storedExpiresAt = stored.stravaExpiresAt;
      const needsRefresh = !storedExpiresAt || Date.now() > (storedExpiresAt - 10 * 60 * 1000);
      if (needsRefresh) {
        const freshToken = await refreshStravaToken();
        if (freshToken) aiCoach.current.stravaToken = freshToken;
      } else if (stored.stravaAccessToken && stored.stravaAccessToken !== aiCoach.current.stravaToken) {
        aiCoach.current.stravaToken = stored.stravaAccessToken;
      }
    } catch {
      // Best-effort; the call below will surface a 401 if it's actually broken.
    }

    const envelope = await aiCoach.current[methodName](userMessage, athleteContext, messages, intent);

    // Session guard — if the user started a new chat while this was
    // in-flight, drop the result silently.
    if (sessionCountRef.current !== sendSession) return true;

    const parsed = parseStructuredEnvelope(envelope, { allowedActions: 'chat' });
    console.log('[CoachActions] Structured parse:', {
      method: methodName,  // 'generateStructuredResponse' or 'generatePhaseSkeleton'
      summaryLen: parsed.contentWithoutBlock?.length,
      proposalsCount: parsed.proposals?.length,
      proposalTypes: parsed.proposals?.map(p => p.type),
      rejectedCount: parsed.rejectedCount,
      rejectedReasons: parsed.rejectedReasons,
      parseError: parsed.parseError,
    });

    const proposals = (parsed.proposals || []).map((p, i) => ({
      ...p,
      proposalId: `${botMsgId}_p${i}`,
      status: 'pending',
      createdAt: Date.now(),
    }));

    setIsTyping(false);
    setMessages(prev => [...prev, {
      id: botMsgId,
      type: 'bot',
      content: parsed.contentWithoutBlock,
      rawContent: JSON.stringify(envelope),  // preserved for history serialization
      proposals,
      rejectedCount: parsed.rejectedCount || 0,
      parseError: parsed.parseError || null,
      timestamp: new Date().toLocaleTimeString(),
    }]);

    return { responseText: parsed.contentWithoutBlock || '' };
  }, [
    recentWorkoutsEnriched,
    tssEnrichedWorkouts,
    athleteGear,
    gearTags,
    workoutData.plannedWorkouts,
    workoutData.recentWorkouts,
    plannedWorkouts,
    athleteProfile,
    coachingMemory,
    coachStyle,
    trainingPlan,
    athleteZones,
    wellnessData,
    userCredentials.activeDataSource,
    refreshStravaToken,
    messages,
  ]);

  // R2: Tool-calling path — mirrors handleStructuredSend but calls
  // generateWithTools and uses dispatchToolCalls instead of parseStructuredEnvelope.
  const handleToolSend = useCallback(async (userMessage, attachedIds, botMsgId, sendSession, toolProfile, intentSource = 'unknown') => {
    if (!aiCoach.current || typeof aiCoach.current.generateWithTools !== 'function') {
      return false;
    }

    // Build athlete context — same shape as handleStructuredSend.
    const recentWorkouts = recentWorkoutsEnriched
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 20);
    const trainingSummary = computeTrainingSummary(tssEnrichedWorkouts, athleteGear, gearTags);
    const athleteContext = {
      recentWorkouts,
      plannedWorkouts: workoutData.plannedWorkouts.slice(0, 7),
      calendarWorkouts: plannedWorkouts,
      athleteProfile,
      trainingSummary,
      coachingMemory,
      coachStyle,
      trainingPlan,
      athleteZones,
      wellnessData,
      completedPlannedWorkouts: plannedWorkouts
        .filter(w => w.status === 'completed' && (w.rpe || w.completionNote))
        .slice(-7),
    };

    if (attachedIds && attachedIds.length > 0) {
      const attached = workoutData.recentWorkouts.filter(w => attachedIds.includes(w.id));
      if (userCredentials.activeDataSource === 'strava' && stravaAPI.current) {
        athleteContext.attachedActivities = await Promise.all(
          attached.map(w => (w.id ? stravaAPI.current.getDetailedWorkout(w.id, w) : Promise.resolve(w)))
        );
      } else {
        athleteContext.attachedActivities = attached;
      }
    }

    try {
      const stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
      const storedExpiresAt = stored.stravaExpiresAt;
      const needsRefresh = !storedExpiresAt || Date.now() > (storedExpiresAt - 10 * 60 * 1000);
      if (needsRefresh) {
        const freshToken = await refreshStravaToken();
        if (freshToken) aiCoach.current.stravaToken = freshToken;
      } else if (stored.stravaAccessToken && stored.stravaAccessToken !== aiCoach.current.stravaToken) {
        aiCoach.current.stravaToken = stored.stravaAccessToken;
      }
    } catch {
      // Best-effort; the call below will surface a 401 if broken.
    }

    const { content, tool_calls } = await aiCoach.current.generateWithTools(
      userMessage, athleteContext, messages, toolProfile
    );

    if (sessionCountRef.current !== sendSession) return true;

    const parsed = dispatchToolCalls(tool_calls, { allowedActions: 'chat', content });
    console.log('[CoachActions] Tool-call dispatch result:', {
      toolProfile,
      proposalsCount: parsed.proposals?.length,
      proposalTypes: parsed.proposals?.map(p => p.type),
      rejectedCount: parsed.rejectedCount,
      rejectedReasons: parsed.rejectedReasons,
    });

    // Telemetry — fire-and-forget, OAuth users only.
    const _stored = JSON.parse(localStorage.getItem('triHarderCredentials') || '{}');
    if (_stored.stravaAthleteId) {
      postCoachActionsTelemetry({
        event: 'dispatch',
        athleteId: _stored.stravaAthleteId,
        intent: toolProfile,
        intentSource,
        proposalsCount: parsed.proposals?.length || 0,
        clarificationCount: parsed.clarifications?.length || 0,
        rejectedCount: parsed.rejectedCount || 0,
        proposalTypes: JSON.stringify(parsed.proposals?.map(p => p.type) || []),
      });
    }

    const proposals = (parsed.proposals || []).map((p, i) => ({
      ...p,
      proposalId: `${botMsgId}_p${i}`,
      status: 'pending',
      createdAt: Date.now(),
    }));

    // request_clarification: the model asked a question instead of acting. Render
    // the validated question as the message body and its options as tap-to-fill
    // suggestion chips. The dispatcher guarantees no proposals coexist with a
    // clarification, so this never competes with ProposalCards.
    const clarification = parsed.clarifications?.[0];
    const baseContent = (parsed.contentWithoutBlock || '').trim();
    const messageContent = clarification
      ? (baseContent && !baseContent.includes(clarification.question)
          ? `${baseContent}\n\n${clarification.question}`
          : clarification.question)
      : parsed.contentWithoutBlock;

    setIsTyping(false);
    setMessages(prev => [...prev, {
      id: botMsgId,
      type: 'bot',
      content: messageContent,
      rawContent: JSON.stringify({ tool_calls, content }),
      proposals,
      suggestions: clarification?.options?.length ? clarification.options : undefined,
      rejectedCount: parsed.rejectedCount || 0,
      parseError: parsed.parseError || null,
      timestamp: new Date().toLocaleTimeString(),
    }]);

    return { responseText: content || '' };
  }, [
    recentWorkoutsEnriched,
    tssEnrichedWorkouts,
    athleteGear,
    gearTags,
    workoutData.plannedWorkouts,
    workoutData.recentWorkouts,
    plannedWorkouts,
    athleteProfile,
    coachingMemory,
    coachStyle,
    trainingPlan,
    athleteZones,
    wellnessData,
    userCredentials.activeDataSource,
    refreshStravaToken,
    messages,
  ]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const newUserMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toLocaleTimeString(),
      attachedActivityIds: attachedActivityIds.length > 0 ? [...attachedActivityIds] : undefined
    };

    setMessages(prev => [...prev, newUserMessage]);
    const currentMessage = inputMessage;
    const currentAttachedIds = [...attachedActivityIds];
    setInputMessage('');
    setAttachedActivityIds([]);

    // Detect coaching report request — open modal instead of sending to AI
    // Only trigger for explicit generation requests, not casual mentions of "report"
    const msgLower = currentMessage.toLowerCase();
    const reportActionWords = ['generate', 'create', 'download', 'build', 'make', 'produce', 'get me', 'give me'];
    const hasReportAction = reportActionWords.some(w => msgLower.includes(w));
    const isReportRequest = (
      (msgLower.includes('report') && hasReportAction && (msgLower.includes('pdf') || msgLower.includes('coaching report') || msgLower.includes('training report'))) ||
      (msgLower.includes('pdf') && hasReportAction)
    );
    if (isReportRequest) {
      setShowReportModal(true);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        content: '📄 Opening your coaching report generator... You can download a PDF with your training analysis, metrics, and personalised recommendations.',
        timestamp: new Date().toLocaleTimeString()
      }]);
      return;
    }

    setIsTyping(true);

    // Capture the session counter so any state mutations from this AI call
    // (mid-stream chunks, finalized message, parsed proposals) are dropped
    // if the user starts a new conversation mid-flight. Same pattern as
    // sessionOpenerFiredRef in the proactive opener path.
    const sendSession = sessionCountRef.current;

    const botMsgId = Date.now();

    // ── Capability dispatch (#4, Jun 2026) ─────────────────────────────
    // The intent classifier NO LONGER gates capability. It is used only to
    // (a) pick the prompt EMPHASIS for the capable tool path and (b) DOWNGRADE
    // to the lighter streaming prose path when the message is confidently pure
    // chat. Anything actionable — or any classifier failure/uncertainty —
    // defaults to the capable native tool-calling path so a real "add / build /
    // change" request can never be silently dropped by a misclassification.
    let intentSource = 'regex';
    const intent = await (async () => {
      if (!aiCoach.current) return 'advice';
      if (typeof aiCoach.current.classifyIntentLLM === 'function') {
        try {
          const result = await aiCoach.current.classifyIntentLLM(currentMessage);
          intentSource = 'llm';
          console.log('[CoachActions] Dispatch (intent source: llm):', result);
          return result;
        } catch (err) {
          console.warn('[CoachActions] LLM classifier failed, falling back to regex:', err);
        }
      }
      const result = typeof aiCoach.current.classifyIntent === 'function'
        ? aiCoach.current.classifyIntent(currentMessage)
        : 'advice';
      console.log('[CoachActions] Dispatch (intent source: regex):', result);
      return result;
    })();

    // "Confident pure chat" = the LLM classifier (not the regex fallback)
    // returned a conversational label. Only this downgrades to the prose path.
    // A regex-fallback label means the LLM call failed, so we do NOT trust it to
    // suppress the capable path — uncertainty biases toward capability.
    // Tradeoff: during an LLM-classifier outage a pure chat message routes to the
    // (non-streaming) tool path; the tool-calling system prompt's "WHEN NOT TO USE
    // TOOLS" rule makes the model answer in prose with zero proposals, so the only
    // cost is a non-streamed answer in that rare case — never a dropped action.
    const CHAT_INTENTS = ['coaching_question', 'coaching_analysis', 'advice'];
    const confidentChat = intentSource === 'llm' && CHAT_INTENTS.includes(intent);

    // Session guard: the classifier call above is async. If the athlete started a
    // new conversation while it was in flight, abort before spending another AI
    // call on a stale message.
    if (sessionCountRef.current !== sendSession) return;

    // Default to the capable native tool-calling path for everything that is not
    // confidently pure chat. tool_choice is 'auto' server-side, so the model
    // still answers in prose (zero proposals) when no action is warranted.
    const useToolCalling =
      aiCoach.current &&
      typeof aiCoach.current.generateWithTools === 'function' &&
      !confidentChat;

    // Structured-output envelope is retained ONLY as a fallback for runtimes that
    // lack native tool calling (fully retiring it is #2). It never runs when the
    // capable tool path is available.
    const useStructured =
      !useToolCalling &&
      !confidentChat &&
      process.env.REACT_APP_USE_STRUCTURED_COACH_ACTIONS !== 'false' &&
      aiCoach.current &&
      (intent === 'plan_build' || intent === 'proposal_bearing' || intent === 'populate_workouts');

    if (useToolCalling) {
      // Intent selects only the prompt EMPHASIS (profile), never capability.
      // Unknown / uncertain / chat labels fall back to the general
      // proposal_bearing emphasis (full toolset, targeted-change framing).
      const VALID_TOOL_PROFILES = ['plan_build', 'proposal_bearing', 'populate_workouts'];
      const toolProfile = VALID_TOOL_PROFILES.includes(intent) ? intent : 'proposal_bearing';
      console.log('[CoachActions] Tool-calling dispatch:', { intent, toolProfile, intentSource });
      try {
        const result = await handleToolSend(currentMessage, currentAttachedIds, botMsgId, sendSession, toolProfile, intentSource);
        if (result) {
          if (result !== true && aiCoach.current) {
            aiCoach.current.extractMemoryUpdate(currentMessage, result.responseText || '', coachingMemory)
              .then(updates => {
                if (!updates) return;
                setCoachingMemory(prev => {
                  const merged = mergeMemory(prev, updates);
                  if (shouldCompact(merged) && aiCoach.current?.compactMemoryNotes) {
                    aiCoach.current.compactMemoryNotes(merged.noteEntries)
                      .then(compacted => {
                        if (!Array.isArray(compacted) || compacted.length === 0) return;
                        setCoachingMemory(cur => applyCompaction(cur, compacted));
                      })
                      .catch(() => {});
                  }
                  return merged;
                });
              })
              .catch(() => {});
          }
          return;
        }
      } catch (err) {
        console.warn('[CoachActions] Tool-calling path failed:', err);
        if (sessionCountRef.current !== sendSession) return;
        // Auth / rate-limit failures will hit the prose path the same way, so
        // surface a clear message and stop. But a 500 (or any other proxy
        // failure) is specific to the tool-calling path — the prose /
        // coach-actions path reliably produces the same multi-surface
        // proposals, so fall THROUGH to it rather than dead-ending the athlete
        // on "didn't come back cleanly".
        if (err?.status === 429 || err?.status === 401) {
          setIsTyping(false);
          const friendly = err.status === 429
            ? "I'm rate-limited on AI requests right now. Give it a minute and try again."
            : "My connection to your training account dropped. Try reconnecting Strava in Settings.";
          setMessages(prev => [...prev, {
            id: botMsgId,
            type: 'bot',
            content: friendly,
            timestamp: new Date().toLocaleTimeString(),
          }]);
          return;
        }
        // Non-auth failure (e.g. tool-path 500) → fall through to the
        // prose/coach-actions path below. Do NOT return and do NOT clear the
        // typing indicator — the prose stream replaces it on its first chunk,
        // and the prose path has its own catch for a clean error if it also fails.
        console.warn('[CoachActions] Falling back to prose/coach-actions path after tool-path failure');
      }
    }

    if (useStructured) {
      const methodName = intent === 'plan_build'
        ? 'generatePhaseSkeleton'
        : 'generateStructuredResponse';
      console.log('[CoachActions] Structured dispatch:', { intent, methodName });
      try {
        const result = await handleStructuredSend(currentMessage, currentAttachedIds, botMsgId, sendSession, methodName, intent);
        if (result) {
          if (result !== true && aiCoach.current) {
            aiCoach.current.extractMemoryUpdate(currentMessage, result.responseText || '', coachingMemory)
              .then(updates => {
                if (!updates) return;
                setCoachingMemory(prev => {
                  const merged = mergeMemory(prev, updates);
                  if (shouldCompact(merged) && aiCoach.current?.compactMemoryNotes) {
                    aiCoach.current.compactMemoryNotes(merged.noteEntries)
                      .then(compacted => {
                        if (!Array.isArray(compacted) || compacted.length === 0) return;
                        setCoachingMemory(cur => applyCompaction(cur, compacted));
                      })
                      .catch(() => {});
                  }
                  return merged;
                });
              })
              .catch(() => {});
          }
          return;
        }
        // result === false means the AICoach instance is missing the
        // requested method entirely (stale bundle?) — fall through to
        // the prose path so the user still gets *something*.
      } catch (err) {
        console.warn('[CoachActions] Structured path failed:', err);
        // Session-guard: if user moved on, drop silently.
        if (sessionCountRef.current !== sendSession) return;
        // Show the athlete a clear, actionable message instead of silently
        // dropping to the prose path. The prose path's rule-based fallback
        // produces misleading replies (e.g. the "training zones" stock
        // message) that hide a real failure from the user. Better to admit
        // the problem and let them retry/rephrase.
        setIsTyping(false);
        const friendly = err?.status === 429
          ? "I'm rate-limited on AI requests right now. Give it a minute and try again."
          : err?.status === 401
            ? "My connection to your training account dropped. Try reconnecting Strava in Settings."
            : "I had trouble preparing your changes — the AI request didn't come back cleanly. Try rephrasing, or hold a minute and ask again.";
        setMessages(prev => [...prev, {
          id: botMsgId,
          type: 'bot',
          content: friendly,
          timestamp: new Date().toLocaleTimeString(),
        }]);
        return;
      }
    }

    let streamStarted = false;
    let streamContent = '';

    // Called for each text chunk during streaming.
    // First chunk: replaces typing indicator with a new streaming message.
    // Subsequent chunks: appends delta to the existing message content.
    const onChunk = (delta) => {
      if (sessionCountRef.current !== sendSession) return; // session switched — drop
      streamContent += delta;
      if (!streamStarted) {
        streamStarted = true;
        setIsTyping(false);
        setMessages(prev => [...prev, {
          id: botMsgId,
          type: 'bot',
          content: streamContent,
          timestamp: new Date().toLocaleTimeString(),
          streaming: true
        }]);
      } else {
        setMessages(prev => prev.map(m =>
          m.id === botMsgId ? { ...m, content: streamContent } : m
        ));
      }
    };

    try {
      const responseMode = intent === 'coaching_question' ? 'direct' : null;
      const responseContent = await generateCoachResponse(currentMessage, onChunk, currentAttachedIds, responseMode, intent);

      // Session guard at finalization — drop silently if user moved on.
      if (sessionCountRef.current !== sendSession) return;

      // Phase 1a: parse the coach-actions envelope additively. Old fenced-block
      // side effects below still run; this just attaches proposals to the
      // message for ProposalCard rendering. Until Phase 1b switches the
      // prompt, parsed.hasBlock will almost always be false and this is a
      // no-op on the user-visible message. After Phase 1b, this is the
      // single canonical mutation path.
      let parsed = null;
      try {
        parsed = parseCoachActions(responseContent, { allowedActions: 'chat' });
      } catch (parseErr) {
        console.warn('[CoachActions] parser threw:', parseErr);
      }
      console.log('[CoachActions] Initial parse:', { responseLen: responseContent?.length, hasBlock: parsed?.hasBlock, proposalsCount: parsed?.proposals?.length, rejectedCount: parsed?.rejectedCount, rejectedReasons: parsed?.rejectedReasons, parseError: parsed?.parseError, responseTail: (responseContent || '').slice(-300) });
      const proposals = (parsed?.proposals || []).map((p, i) => ({
        ...p,
        proposalId: `${botMsgId}_p${i}`,
        status: 'pending',
        createdAt: Date.now(),
      }));
      const displayContent = parsed?.hasBlock ? parsed.contentWithoutBlock : responseContent;
      const rejectedCount = parsed?.rejectedCount || 0;
      const parseError = parsed?.parseError || null;

      if (streamStarted) {
        // Streaming path — finalize with parsed prose + attached proposals
        setMessages(prev => prev.map(m =>
          m.id === botMsgId ? {
            ...m,
            streaming: false,
            content: displayContent,
            rawContent: responseContent,
            proposals,
            rejectedCount,
            parseError,
          } : m
        ));
      } else {
        // Non-streaming path (non-AI responses) — add the complete message
        setMessages(prev => [...prev, {
          id: botMsgId,
          type: 'bot',
          content: displayContent,
          rawContent: responseContent,
          proposals,
          rejectedCount,
          parseError,
          timestamp: new Date().toLocaleTimeString()
        }]);
      }

      // Note: the prose-path targeted-retry block was removed in Commit D
      // (May 2026). It existed to coax DeepSeek-V3.2 into emitting a
      // coach-actions block when it produced prose-only responses for
      // action requests. With the structured-output path now handling all
      // proposal-bearing requests via json_schema strict mode, the retry
      // is dead weight. If the structured path itself fails, the user
      // sees a friendly error message (in handleSendMessage above) rather
      // than this best-effort retry chain.
    } catch (error) {
      if (streamStarted) {
        setMessages(prev => prev.map(m =>
          m.id === botMsgId
            ? { ...m, streaming: false, content: streamContent + '\n\n⚠️ Response was interrupted. Please try again.' }
            : m
        ));
      } else {
        setMessages(prev => [...prev, {
          id: botMsgId,
          type: 'bot',
          content: "Sorry, I'm having trouble processing your request right now. Please try again.",
          timestamp: new Date().toLocaleTimeString()
        }]);
      }
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle quick answer selection for profile completion
  const handleQuickAnswer = (answer) => {
    setInputMessage(answer);
    // Auto-send the message after a brief delay to allow UI update
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const quickActions = [
    { icon: Activity, text: "Analyze recent training",      action: "Analyze my recent training and give me feedback" },
    { icon: Target,   text: "Create training plan",         action: "Help me create a training plan for my next race" },
    { icon: FileText, text: "Generate coaching report",     action: "report" },
    { icon: Sun,      text: "Feeling fresh — challenge me", action: "I am feeling fresh and want a challenge today. What workout would you recommend?" },
    { icon: Moon,     text: "Feeling tired & sluggish",     action: "I am feeling tired and sluggish today. What should I do for training?" },
    { icon: Calendar, text: "Clean up my calendar",         action: "Please review my training calendar and remove any duplicate or excess planned workouts. Keep only the most suitable sessions and remove any that look like accidental repeats from running the training plan multiple times." },
    { icon: User,     text: "Update my profile",            action: "profile" },
  ];

  const handleQuickAction = (action) => {
    if (action === "profile") {
      setCurrentPage(action);
    } else if (action === "report") {
      setShowReportModal(true);
    } else {
      setInputMessage(action);
    }
  };

  // Profile completion questions
  const profileQuestions = [
    {
      key: 'races',
      question: "Are you currently training for any races? If so, tell me about these races and when they happen. If not, just let me know you're training for general fitness.",
      followUp: "Great! Now I know about your race goals.",
      quickAnswers: [
        "No races planned - training for general fitness",
        "Sprint triathlon in 3-4 months",
        "Olympic triathlon this season",
        "Half Ironman next year",
        "Full Ironman - long term goal"
      ]
    },
    {
      key: 'goals',
      question: "What are your main goals for your training and races? For example: finish your first triathlon, improve your time, work on specific weaknesses, or just stay fit and enjoy the sport.",
      followUp: "Excellent! Your goals will help me tailor your training.",
      quickAnswers: [
        "Finish my first triathlon",
        "Improve my personal best times",
        "Work on swim technique",
        "Build endurance and stamina",
        "Stay fit and enjoy the sport"
      ]
    },
    {
      key: 'experience',
      question: "What's your experience level with triathlons? Are you a complete beginner, have you done a few races, or are you more experienced? Also, which discipline (swim, bike, run) do you feel strongest/weakest in?",
      followUp: "Perfect! Understanding your experience helps me design appropriate training.",
      quickAnswers: [
        "Complete beginner - new to triathlon",
        "Done 1-2 races - still learning",
        "Experienced - 5+ races completed",
        "Swimming is my weakness",
        "Running is my strength"
      ]
    },
    {
      key: 'weeklyHours',
      question: "How many hours can you realistically devote to training each week? Please be honest about your schedule constraints - it's better to be consistent with fewer hours than to overcommit.",
      followUp: "Fantastic! That gives me a clear picture of your training availability.",
      quickAnswers: [
        "1-3 hours per week",
        "4-6 hours per week", 
        "7-10 hours per week",
        "11-15 hours per week",
        "16+ hours per week"
      ]
    }
  ];

  // Handle profile completion
  const handleProfileCompletion = (userMessage) => {
    const message = userMessage.toLowerCase();
    
    // Check if user wants to start profile completion
    if (message.includes('complete my athlete profile') || message.includes('help me complete my athlete profile')) {
      setProfileCompletion({
        isActive: true,
        currentStep: 0,
        answers: {}
      });
      
      return `I'd love to help you complete your athlete profile! This will help me give you much better, personalized coaching advice. I'll ask you a few questions one at a time.\n\n**Question 1 of ${profileQuestions.length}:**\n\n${profileQuestions[0].question}`;
    }
    
    // Handle ongoing profile completion
    if (profileCompletion.isActive && profileCompletion.currentStep < profileQuestions.length) {
      const currentQuestion = profileQuestions[profileCompletion.currentStep];
      const newAnswers = {
        ...profileCompletion.answers,
        [currentQuestion.key]: userMessage
      };
      
      const nextStep = profileCompletion.currentStep + 1;
      
      if (nextStep < profileQuestions.length) {
        // More questions to go
        setProfileCompletion({
          isActive: true,
          currentStep: nextStep,
          answers: newAnswers
        });
        
        return `${currentQuestion.followUp}\n\n**Question ${nextStep + 1} of ${profileQuestions.length}:**\n\n${profileQuestions[nextStep].question}`;
      } else {
        // Profile completion finished
        const completedProfile = {
          races: newAnswers.races || '',
          goals: newAnswers.goals || '',
          experience: newAnswers.experience || '',
          weeklyHours: newAnswers.weeklyHours || '',
          isCompleted: true
        };
        
        setAthleteProfile(completedProfile);
        setProfileCompletion({
          isActive: false,
          currentStep: 0,
          answers: {}
        });

        // Save profile to localStorage
        const updatedCredentials = {
          ...userCredentials,
          athleteProfile: completedProfile
        };
        localStorage.setItem('triHarderCredentials', JSON.stringify(updatedCredentials));

        // Generate AI summary in the background (non-blocking)
        generateAndSaveProfileSummary(completedProfile, coachingMemory);
        
        return `🎉 **Profile Complete!** Thank you for taking the time to share this information with me!\n\n**Here's your athlete profile summary:**\n\n🏁 **Races:** ${completedProfile.races}\n🎯 **Goals:** ${completedProfile.goals}\n💪 **Experience:** ${completedProfile.experience}\n⏰ **Weekly Training Hours:** ${completedProfile.weeklyHours}\n\nNow I can provide much more personalized coaching advice based on your specific situation, goals, and time constraints. Feel free to ask me anything about training plans, workout analysis, or race preparation!`;
      }
    }
    
    return null; // Not a profile completion message
  };

  // Detect if we're currently processing a Strava OAuth callback
  const isProcessingCallback = !!(new URLSearchParams(location.search).get('code') && new URLSearchParams(location.search).get('state'));

  // Unauthenticated shell: standalone pages + splash gate
  if (showSplash) {
    return (
      <div className={theme === 'light' ? 'light' : ''}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/privacy" element={<PrivacyPage standalone />} />
          <Route path="/support" element={<SupportPage standalone />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={
            <SplashPage
              onConnect={connectStrava}
              onConnectIntervals={connectIntervalsOAuth}
              onConnectApiKey={handleConnectApiKey}
              onDemo={startDemoMode}
              onEnter={() => setShowSplash(false)}
              isConnected={userCredentials.isStravaConfigured || userCredentials.isIntervalsOAuthConfigured || userCredentials.isConfigured}
              isProcessingCallback={isProcessingCallback}
              intervalsOAuthAvailable={true}
            />
          } />
        </Routes>
      </Suspense>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-dvh max-h-dvh w-full mx-auto bg-surface overflow-x-hidden ${
      theme === 'light' ? 'light' : ''}`}>

      {/* Privacy Consent Banner — shown on first visit */}
      {showConsentBanner && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-fg-disabled mb-2">Before we start coaching...</h2>
            <p className="text-fg-disabled mb-5 text-sm leading-relaxed">
              Tri Harder remembers your goals, race dates, and training context so coaching stays
              relevant across sessions. When connected via Strava or intervals.icu OAuth, coaching
              memory and (optionally) conversation history are synced to a secure server so you can
              pick up on any device.
            </p>
            <div className="space-y-3 mb-5">
              <button
                onClick={() => handleConsentChoice('full')}
                className="w-full p-4 border-2 border-border-focus bg-primary-50 rounded-lg text-left hover:bg-primary-100 transition-colors"
              >
                <p className="font-semibold text-feedback-info-text">Save my coaching history</p>
                <p className="text-sm text-action-hover mt-0.5">Conversations and coaching notes persist between sessions on this device.</p>
              </button>
              <button
                onClick={() => handleConsentChoice('session')}
                className="w-full p-4 border border-neutral-300 rounded-lg text-left hover:bg-neutral-50 transition-colors"
              >
                <p className="font-semibold text-fg-disabled">This session only — don't save chats</p>
                <p className="text-sm text-fg-subtle mt-0.5">Conversation cleared when you close this tab. API credentials and coaching notes still saved.</p>
              </button>
            </div>
            <p className="text-xs text-fg-muted">
              You can change this anytime in Settings → Privacy &amp; Data.{' '}
              <button
                onClick={() => { handleConsentChoice('session'); setCurrentPage('privacy'); }}
                className="underline hover:text-fg-disabled"
              >
                Privacy Policy
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Demo mode banner */}
      {isDemoMode && (
        <div className="bg-accent-purple-500/70 border-b border-accent-purple-500 px-4 py-2 flex items-center justify-between flex-shrink-0">
          <p className="text-fg-on-action text-xs">
            Demo mode — showing sample training data. Connect a training account to use your own data.
          </p>
          <button
            onClick={exitDemoMode}
            className="text-fg-on-action text-xs ml-4 underline transition-opacity hover:opacity-80 flex-shrink-0"
          >
            Exit demo
          </button>
        </div>
      )}

      {/* Local-only mode banner — shown when using intervals.icu API key (no cloud sync) */}
      {userCredentials.isConfigured && !userCredentials.isIntervalsOAuthConfigured && !userCredentials.isStravaConfigured && (
        <div className="bg-feedback-warning-bg border-b border-feedback-warning-border px-4 py-2 flex items-center justify-between flex-shrink-0">
          <p className="text-feedback-warning-text text-xs leading-relaxed">
            Using intervals.icu API key — coaching data is stored on this device only.
            {true && (
              <>
                {' '}
                <button
                  onClick={connectIntervalsOAuth}
                  className="underline font-medium hover:text-feedback-warning-text transition-colors"
                >
                  Connect via OAuth for cloud sync →
                </button>
              </>
            )}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="bg-surface-subtle px-4 py-3 shadow-lg flex-shrink-0">
        <div className="flex items-center justify-between">
          {/* Brand — click logo to return to splash/home */}
          <div className="flex items-center space-x-2.5">
            <button
              onClick={() => setCurrentPage('about')}
              title="About Tri Harder"
              className="bg-white rounded-lg px-2 py-1 flex-shrink-0 hover:opacity-80 transition-opacity active:scale-95"
            >
              <img src={logoSrc} alt="Tri Harder" className="h-6 w-auto" />
            </button>
            <div className="hidden sm:block">
              <p className="text-xs leading-tight">
                {workoutData.isLoading ? (
                  <span className="text-fg-muted">Syncing...</span>
                ) : userCredentials.activeDataSource !== 'none' ? (
                  <span className="text-feedback-success-icon">
                    ● {workoutData.recentWorkouts.length > 0 ? `${workoutData.recentWorkouts.length} activities synced` : 'Connected'}
                  </span>
                ) : (
                  <span className="text-fg-subtle">Connect a training account in Settings</span>
                )}
              </p>
            </div>
            {nextRace && nextRace.daysUntil <= 90 && (
              <button
                onClick={() => setCurrentPage('dashboard')}
                className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  nextRace.daysUntil <= 14
                    ? 'bg-feedback-warning-bg/40 border-feedback-warning-border/60 text-feedback-warning-text hover:bg-feedback-warning-bg/60'
                    : nextRace.daysUntil <= 42
                    ? 'bg-feedback-warning-bg/40 border-feedback-warning-border/60 text-feedback-warning-text hover:bg-feedback-warning-bg/60'
                    : 'bg-surface-raised border-border-strong text-fg-secondary hover:bg-surface-hover'
                }`}
              >
                🏁 {nextRace.daysUntil}d — {(nextRace.name || nextRace.distance || 'Race').substring(0, 20)}
              </button>
            )}
          </div>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="p-1.5 rounded-lg text-fg-muted hover:text-fg hover:bg-surface-raised transition-colors flex-shrink-0"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Body: left sidebar (desktop) + content */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left Nav Sidebar — desktop md+ only */}
        <nav className={`hidden md:flex flex-col flex-shrink-0 bg-surface-subtle border-r border-border transition-all duration-200 ${navSidebarCollapsed ? 'w-14' : 'w-44'}`}>
          <div className="flex flex-col flex-1 py-2 gap-0.5 overflow-y-auto">
            {[
              { page: 'dashboard', icon: Activity, label: 'Dashboard' },
              { page: 'calendar', icon: Calendar, label: 'Calendar' },
              { page: 'trainingplan', icon: Target, label: 'Plan' },
              { page: 'activities', icon: List, label: 'Activities' },
              { page: 'strength', icon: Dumbbell, label: 'Strength' },
              { page: 'profile', icon: User, label: 'Profile' },
              { page: 'settings', icon: Settings, label: 'Settings' },
            ].map(({ page, icon: Icon, label }) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                title={label}
                className={`flex items-center gap-3 mx-2 px-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === page ? 'bg-surface-hover text-fg' : 'text-fg-muted hover:text-fg hover:bg-surface-raised'
                } ${navSidebarCollapsed ? 'justify-center' : ''}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!navSidebarCollapsed && <span>{label}</span>}
              </button>
            ))}
          </div>
          <button
            onClick={() => setNavSidebarCollapsed(v => !v)}
            className="flex items-center justify-center mx-2 mb-3 px-2 py-2 rounded-lg text-fg-subtle hover:text-fg-secondary hover:bg-surface-raised transition-colors"
            title={navSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {navSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </nav>

        {/* Main content wrapper */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0 min-h-0 pb-16 md:pb-0">

          {dataError && (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-feedback-danger-bg/60 border-b border-feedback-danger-border/70 text-sm text-feedback-danger-text flex-shrink-0">
              <span className="text-base flex-shrink-0">⚠️</span>
              <span className="flex-1 text-xs leading-snug">{dataError}</span>
              <button
                onClick={() => setDataError('')}
                className="text-feedback-danger-icon hover:text-feedback-danger-text flex-shrink-0 text-xs underline ml-2"
              >
                Dismiss
              </button>
            </div>
          )}

      <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Chat — full page (mobile) */}
        <Route path="/chat" element={
          <ChatPanel
            messages={messages}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            handleSendMessage={handleSendMessage}
            handleKeyPress={handleKeyPress}
            isTyping={isTyping}
            messagesEndRef={messagesEndRef}
            startNewConversation={startNewConversation}
            profileCompletion={profileCompletion}
            profileQuestions={profileQuestions}
            handleQuickAnswer={handleQuickAnswer}
            quickActions={quickActions}
            onQuickAction={handleQuickAction}
            sessionHistory={sessionHistory}
            recentActivities={workoutData.recentWorkouts || []}
            attachedActivityIds={attachedActivityIds}
            onAttachedActivityIdsChange={setAttachedActivityIds}
            savedQuestions={savedQuestions}
            onSavedQuestionsChange={saveSavedQuestions}
                  onResolveCheckin={resolveCheckin}
                  onConfirmProposal={handleConfirmProposal}
                  onConfirmAllProposals={handleConfirmAllProposals}
                  onDiscardProposal={handleDiscardProposal}
          />
        } />

        {/* Calendar with chat sidebar */}
        <Route path="/calendar" element={
          <div className="flex-1 flex overflow-hidden">
            <CalendarPage
              workouts={plannedWorkouts}
              onComplete={handleWorkoutComplete}
              onSkip={handleWorkoutSkip}
              onEdit={handleWorkoutEdit}
              onDelete={handleWorkoutDelete}
              onReschedule={handleWorkoutReschedule}
            />
            {chatSidebarOpen && (
              <div className={`hidden lg:flex lg:flex-col flex-shrink-0 border-l border-neutral-200 bg-neutral-50 overflow-hidden transition-all duration-200 ${chatSidebarExpanded ? 'w-[560px] xl:w-[640px]' : 'w-[340px] xl:w-[380px]'}`}>
                <ChatPanel
                  messages={messages}
                  inputMessage={inputMessage}
                  setInputMessage={setInputMessage}
                  handleSendMessage={handleSendMessage}
                  handleKeyPress={handleKeyPress}
                  isTyping={isTyping}
                  messagesEndRef={messagesEndRef}
                  startNewConversation={startNewConversation}
                  profileCompletion={profileCompletion}
                  profileQuestions={profileQuestions}
                  handleQuickAnswer={handleQuickAnswer}
                  quickActions={quickActions}
                  onQuickAction={handleQuickAction}
                  sidebar
                  expanded={chatSidebarExpanded}
                  onToggleExpand={() => setChatSidebarExpanded(v => !v)}
                  onClose={() => { setChatSidebarOpen(false); setChatSidebarExpanded(false); }}
                  sessionHistory={sessionHistory}
                  recentActivities={workoutData.recentWorkouts || []}
                  attachedActivityIds={attachedActivityIds}
                  onAttachedActivityIdsChange={setAttachedActivityIds}
                  savedQuestions={savedQuestions}
                  onSavedQuestionsChange={saveSavedQuestions}
                  onResolveCheckin={resolveCheckin}
                  onConfirmProposal={handleConfirmProposal}
                  onConfirmAllProposals={handleConfirmAllProposals}
                  onDiscardProposal={handleDiscardProposal}
                />
              </div>
            )}
          </div>
        } />

        {/* Training Plan with chat sidebar on desktop */}
        <Route path="/trainingplan" element={
          <div className="flex-1 flex overflow-hidden">
            <TrainingPlanPage
              trainingPlan={trainingPlan}
              coachingMemory={coachingMemory}
              setInputMessage={setInputMessage}
            />
            {chatSidebarOpen && (
              <div className={`hidden lg:flex lg:flex-col flex-shrink-0 border-l border-neutral-200 bg-neutral-50 overflow-hidden transition-all duration-200 ${chatSidebarExpanded ? 'w-[560px] xl:w-[640px]' : 'w-[340px] xl:w-[380px]'}`}>
                <ChatPanel
                  messages={messages}
                  inputMessage={inputMessage}
                  setInputMessage={setInputMessage}
                  handleSendMessage={handleSendMessage}
                  handleKeyPress={handleKeyPress}
                  isTyping={isTyping}
                  messagesEndRef={messagesEndRef}
                  startNewConversation={startNewConversation}
                  profileCompletion={profileCompletion}
                  profileQuestions={profileQuestions}
                  handleQuickAnswer={handleQuickAnswer}
                  quickActions={quickActions}
                  onQuickAction={handleQuickAction}
                  sidebar
                  expanded={chatSidebarExpanded}
                  onToggleExpand={() => setChatSidebarExpanded(v => !v)}
                  onClose={() => { setChatSidebarOpen(false); setChatSidebarExpanded(false); }}
                  sessionHistory={sessionHistory}
                  recentActivities={workoutData.recentWorkouts || []}
                  attachedActivityIds={attachedActivityIds}
                  onAttachedActivityIdsChange={setAttachedActivityIds}
                  savedQuestions={savedQuestions}
                  onSavedQuestionsChange={saveSavedQuestions}
                  onResolveCheckin={resolveCheckin}
                  onConfirmProposal={handleConfirmProposal}
                  onConfirmAllProposals={handleConfirmAllProposals}
                  onDiscardProposal={handleDiscardProposal}
                />
              </div>
            )}
          </div>
        } />

        {/* Activities with chat sidebar */}
        <Route path="/activities" element={
          <div className="flex-1 flex overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              <ActivitiesPage
                userCredentials={userCredentials}
                workoutData={workoutData}
                enrichedWorkouts={tssEnrichedWorkouts}
                getMockData={getMockData}
                loadWorkoutData={loadWorkoutData}
                pwrdByStravaWhiteApp={pwrdByStravaWhiteApp}
                unratedNewActivities={unratedNewActivities}
                activityRatings={activityRatings}
                onRateActivity={rateActivity}
                onDismissRpe={dismissRpePrompt}
                onOpenActivityDetail={setSelectedActivityForDetail}
              />
            </div>
            {chatSidebarOpen && (
              <div className={`hidden lg:flex lg:flex-col flex-shrink-0 border-l border-neutral-200 bg-neutral-50 overflow-hidden transition-all duration-200 ${chatSidebarExpanded ? 'w-[560px] xl:w-[640px]' : 'w-[340px] xl:w-[380px]'}`}>
                <ChatPanel
                  messages={messages}
                  inputMessage={inputMessage}
                  setInputMessage={setInputMessage}
                  handleSendMessage={handleSendMessage}
                  handleKeyPress={handleKeyPress}
                  isTyping={isTyping}
                  messagesEndRef={messagesEndRef}
                  startNewConversation={startNewConversation}
                  profileCompletion={profileCompletion}
                  profileQuestions={profileQuestions}
                  handleQuickAnswer={handleQuickAnswer}
                  quickActions={quickActions}
                  onQuickAction={handleQuickAction}
                  sidebar
                  expanded={chatSidebarExpanded}
                  onToggleExpand={() => setChatSidebarExpanded(v => !v)}
                  onClose={() => { setChatSidebarOpen(false); setChatSidebarExpanded(false); }}
                  sessionHistory={sessionHistory}
                  recentActivities={workoutData.recentWorkouts || []}
                  attachedActivityIds={attachedActivityIds}
                  onAttachedActivityIdsChange={setAttachedActivityIds}
                  savedQuestions={savedQuestions}
                  onSavedQuestionsChange={saveSavedQuestions}
                  onResolveCheckin={resolveCheckin}
                  onConfirmProposal={handleConfirmProposal}
                  onConfirmAllProposals={handleConfirmAllProposals}
                  onDiscardProposal={handleDiscardProposal}
                />
              </div>
            )}
          </div>
        } />

        {/* Profile */}
        <Route path="/profile" element={
          <ProfilePage
            profile={athleteProfile}
            onSave={handleProfileSave}
            userScope={getUserScope(userCredentials, isDemoMode)}
            stravaImportedFields={stravaImportedFields}
            onClearStravaImport={() => setStravaImportedFields(null)}
          />
        } />

        {/* Settings */}
        <Route path="/settings" element={
          <div className="flex-1 overflow-y-auto">
            <SettingsPage
              userCredentials={userCredentials}
              workoutData={workoutData}
              coachStyle={coachStyle}
              coachingMemory={coachingMemory}
              privacyMode={privacyMode}
              athleteGear={athleteGear}
              gearTags={gearTags}
              plannedWorkouts={plannedWorkouts}
              athleteProfile={athleteProfile}
              clearCredentials={clearCredentials}
              connectStrava={connectStrava}
              switchDataSource={switchDataSource}
              setCoachStyle={setCoachStyle}
              setCoachingMemory={setCoachingMemory}
              setMessages={setMessages}
              setPlannedWorkouts={setPlannedWorkouts}
              setGearTags={setGearTags}
              handleConsentChoice={handleConsentChoice}
              analyticsOptIn={analyticsOptIn}
              handleAnalyticsOptInChange={handleAnalyticsOptInChange}
              setAthleteProfile={setAthleteProfile}
              EMPTY_MEMORY={EMPTY_MEMORY}
              compactMemoryNow={compactMemoryNow}
              deleteMemoryNote={deleteMemoryNote}
              deleteRace={deleteRace}
              deleteInjury={deleteInjury}
              clearCoachingMemory={clearCoachingMemory}
            />
          </div>
        } />

        {/* Info pages */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/admin" element={<AdminPage />} />

        {/* Strength */}
        <Route path="/strength" element={<StrengthPage onAskCoach={handleAskCoach} />} />

        {/* Dashboard — default + explicit */}
        <Route path="/dashboard" element={
          <DashboardPage workouts={tssEnrichedWorkouts} isLoading={!hasInitializedAppData || workoutData.isLoading} hasConnectedSource={userCredentials.activeDataSource !== 'none'} sportFocus={athleteProfile.background?.sportFocus} races={coachingMemory.races} gear={athleteGear} gearTags={gearTags} onGenerateReport={() => setShowReportModal(true)} onNavigateStrength={() => setCurrentPage('strength')} coachBriefing={coachBriefing} coachBriefingLoading={coachBriefingLoading} onRegenerateBriefing={() => { briefingFiredRef.current = false; generateBriefingForDashboard(true); }} onAskCoach={handleAskCoach} />
        } />
        <Route path="*" element={
          <DashboardPage workouts={tssEnrichedWorkouts} isLoading={!hasInitializedAppData || workoutData.isLoading} hasConnectedSource={userCredentials.activeDataSource !== 'none'} sportFocus={athleteProfile.background?.sportFocus} races={coachingMemory.races} gear={athleteGear} gearTags={gearTags} onGenerateReport={() => setShowReportModal(true)} onNavigateStrength={() => setCurrentPage('strength')} coachBriefing={coachBriefing} coachBriefingLoading={coachBriefingLoading} onRegenerateBriefing={() => { briefingFiredRef.current = false; generateBriefingForDashboard(true); }} onAskCoach={handleAskCoach} />
        } />
      </Routes>
      </Suspense>

        </div>{/* /main content wrapper */}
      </div>{/* /body */}

      {/* Bottom Nav — mobile only, fixed to viewport bottom */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-surface-subtle border-t border-border flex items-stretch [padding-bottom:env(safe-area-inset-bottom)]">
        {[
          { page: 'chat', icon: MessageSquare, label: 'Coach' },
          { page: 'dashboard', icon: Activity, label: 'Dashboard' },
          { page: 'calendar', icon: Calendar, label: 'Calendar' },
          { page: 'activities', icon: List, label: 'Activities' },
        ].map(({ page, icon: Icon, label }) => (
          <button
            key={page}
            onClick={() => { setMoreMenuOpen(false); setCurrentPage(page); }}
            className={`flex-1 flex flex-col items-center justify-center gap-1.5 py-3 transition-colors active:scale-95 ${
              currentPage === page ? 'text-fg' : 'text-fg-subtle active:text-fg-secondary'
            }`}
          >
            <Icon className="w-6 h-6" />
            <span className="text-[10px] font-medium leading-none">{label}</span>
          </button>
        ))}
        {/* More button */}
        <button
          onClick={() => setMoreMenuOpen(v => !v)}
          className={`flex-1 flex flex-col items-center justify-center gap-1.5 py-3 transition-colors active:scale-95 ${
            moreMenuOpen || ['strength', 'profile', 'settings', 'about', 'support'].includes(currentPage)
              ? 'text-fg'
              : 'text-fg-subtle active:text-fg-secondary'
          }`}
        >
          <MoreHorizontal className="w-6 h-6" />
          <span className="text-[10px] font-medium leading-none">More</span>
        </button>
      </nav>

      {/* More drawer — mobile only */}
      {moreMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/40"
            onClick={() => setMoreMenuOpen(false)}
          />
          {/* Drawer */}
          <div className="md:hidden fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom))] inset-x-0 z-50 bg-surface-subtle border-t border-border rounded-t-2xl shadow-2xl">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <span className="text-fg-secondary text-xs font-semibold uppercase tracking-wide">More</span>
              <button onClick={() => setMoreMenuOpen(false)} className="text-fg-subtle hover:text-fg p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 px-4 pb-6 pt-2">
              {[
                { page: 'strength', icon: Dumbbell, label: 'Strength' },
                { page: 'profile', icon: User, label: 'Profile' },
                { page: 'settings', icon: Settings, label: 'Settings' },
                { page: 'about', icon: FileText, label: 'About' },
                { page: 'support', icon: MessageSquare, label: 'Support' },
              ].map(({ page, icon: Icon, label }) => (
                <button
                  key={page}
                  onClick={() => { setMoreMenuOpen(false); setCurrentPage(page); }}
                  className={`flex flex-col items-center gap-2 py-4 px-2 rounded-xl border transition-colors ${
                    currentPage === page
                      ? 'bg-surface-hover border-border-strong text-fg'
                      : 'bg-surface-raised border-border text-fg-secondary hover:bg-surface-hover hover:text-fg'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[11px] font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Workout completion modal */}
      {completionModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-surface-raised border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-fg font-semibold text-lg mb-1">How did it go?</h3>
            <p className="text-fg-muted text-sm mb-5 truncate">
              {plannedWorkouts.find(w => w.id === completionModal.workoutId)?.title || 'Workout'}
            </p>

            {/* RPE Selector */}
            <p className="text-fg-secondary text-xs font-medium mb-2 uppercase tracking-wide">Effort (RPE)</p>
            <div className="flex gap-2 mb-1">
              {[1, 2, 3, 4, 5].map(val => (
                <button
                  key={val}
                  onClick={() => setCompletionModal(prev => ({ ...prev, rpe: val }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold border transition-colors ${
                    completionModal.rpe === val
                      ? 'bg-action border-border-focus text-fg-on-action'
                      : 'bg-surface-hover border-border-strong text-fg-secondary hover:bg-neutral-600'
                  }`}
                >
                  {val}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mb-5 text-center">
              {['Fresh', 'Comfortable', 'Solid', 'Hard', 'Cooked'].map((label, i) => (
                <span key={i} className="flex-1 text-fg-subtle text-[10px] leading-tight">{label}</span>
              ))}
            </div>

            {/* Note */}
            <textarea
              value={completionModal.note}
              onChange={e => setCompletionModal(prev => ({ ...prev, note: e.target.value }))}
              placeholder="Any notes? e.g. felt strong, legs heavy..."
              rows={2}
              className="w-full bg-surface-hover border border-border-strong rounded-lg px-3 py-2 text-sm text-fg placeholder-fg-subtle focus:outline-none focus:border-border-focus resize-none mb-4"
            />

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setCompletionModal(prev => ({ ...prev, rpe: null, note: '' }));
                  handleCompletionConfirm();
                }}
                className="flex-1 py-2 rounded-lg bg-surface-hover hover:bg-neutral-600 text-fg-secondary text-sm font-medium transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleCompletionConfirm}
                className="flex-1 py-2 rounded-lg bg-action hover:bg-action-hover text-fg-on-action text-sm font-medium transition-colors"
              >
                Complete ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating chat button — desktop only; mobile uses bottom nav Coach item */}
      <div className="hidden md:contents">
      {!['chat'].includes(currentPage) && (
        (['calendar', 'activities', 'trainingplan'].includes(currentPage) ? !chatSidebarOpen : true) && (
          <>
            {!chatOverlayOpen && (
              <ChatToggleButton
                onClick={() => {
                  if (['calendar', 'activities', 'trainingplan'].includes(currentPage)) {
                    setChatSidebarOpen(true);
                  } else {
                    setChatOverlayOpen(true);
                  }
                }}
              />
            )}
            {chatOverlayOpen && !['calendar', 'activities', 'trainingplan'].includes(currentPage) && (
              <ChatOverlay onClose={() => setChatOverlayOpen(false)} messages={messages} startNewConversation={startNewConversation} sessionHistory={sessionHistory}>
                <ChatPanel
                  messages={messages}
                  inputMessage={inputMessage}
                  setInputMessage={setInputMessage}
                  handleSendMessage={handleSendMessage}
                  handleKeyPress={handleKeyPress}
                  isTyping={isTyping}
                  messagesEndRef={messagesEndRef}
                  startNewConversation={startNewConversation}
                  profileCompletion={profileCompletion}
                  profileQuestions={profileQuestions}
                  handleQuickAnswer={handleQuickAnswer}
                  quickActions={quickActions}
                  onQuickAction={handleQuickAction}
                  sidebar
                  sessionHistory={sessionHistory}
                  recentActivities={workoutData.recentWorkouts || []}
                  attachedActivityIds={attachedActivityIds}
                  onAttachedActivityIdsChange={setAttachedActivityIds}
                  savedQuestions={savedQuestions}
                  onSavedQuestionsChange={saveSavedQuestions}
                  onResolveCheckin={resolveCheckin}
                  onConfirmProposal={handleConfirmProposal}
                  onConfirmAllProposals={handleConfirmAllProposals}
                  onDiscardProposal={handleDiscardProposal}
                />
              </ChatOverlay>
            )}
          </>
        )
      )}
      </div>{/* /floating chat wrapper */}

      {/* Report generation modal */}
      <Suspense fallback={null}>
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          aiCoach={aiCoach.current}
          athleteProfile={athleteProfile}
          coachingMemory={coachingMemory}
          workoutData={workoutData}
          trainingSummary={computeTrainingSummary(tssEnrichedWorkouts, athleteGear, gearTags)}
          trainingPlan={trainingPlan}
          plannedWorkouts={plannedWorkouts}
          coachStyle={coachStyle}
          athleteGear={athleteGear}
          gearTags={gearTags}
        />
      </Suspense>

      {/* Activity detail insight card (Issue #9) — fetches detailed/stream data on demand */}
      <Suspense fallback={null}>
        <ActivityDetailModal
          activity={selectedActivityForDetail}
          onClose={() => setSelectedActivityForDetail(null)}
          stravaAPI={stravaAPI.current}
          athleteProfile={athleteProfile}
          coachingMemory={coachingMemory}
          recentWorkouts={tssEnrichedWorkouts}
          aiCoach={aiCoach.current}
          onAskCoach={handleAskCoach}
        />
      </Suspense>

      {/* First-run welcome modal — three "what next?" cards */}
      {showWelcomeModal && (
        <Suspense fallback={null}>
          <WelcomeModal
            goal={pendingWelcomeGoal}
            onDismiss={dismissWelcomeModal}
            onGoToProfile={() => { dismissWelcomeModal(); navigate('/profile'); }}
            onGoToDashboard={() => { dismissWelcomeModal(); navigate('/dashboard'); }}
            onStartChat={() => {
              const goal = pendingWelcomeGoal;
              const prefill =
                goal === 'firstTri'      ? "I'm preparing for my first triathlon. Where should I start?" :
                goal === 'runningRace'   ? "I'm focused on my running. Help me build a plan that fits my goals — whether that's a race or just getting fitter." :
                goal === 'improving'     ? "I want to get faster. Analyse my recent training and tell me what to focus on." :
                                            "Give me a quick tour of what you can help me with.";
              setInputMessage(prefill);
              // Mark session opener as fired so it doesn't race with the user's prompt
              sessionOpenerFiredRef.current = true;
              dismissWelcomeModal();
              navigate('/chat');
            }}
          />
        </Suspense>
      )}
    </div>
  );
};

// Exported for focused unit testing (see App.profileSync.test.js) — not used
// elsewhere in the app outside this module.
export { computeTouchedProfileFields, profileFieldValuesEqual, isEmptyProfileFieldValue };
export default TriathlonCoachApp;
